// src/enums.ts
import { z } from "zod";
var USER_ROLES = ["user", "admin"];
var UserRole = z.enum(USER_ROLES);
var INVITE_KINDS = ["master", "personal", "admin"];
var InviteKind = z.enum(INVITE_KINDS);
var INVITE_REQUEST_STATUSES = ["pending", "approved", "rejected"];
var InviteRequestStatus = z.enum(INVITE_REQUEST_STATUSES);
var PERSONAL_INVITE_GRANT = 5;
var TRIP_VISIBILITIES = ["private", "link", "public"];
var TripVisibility = z.enum(TRIP_VISIBILITIES);
var TRIP_STATUSES = ["draft", "planned", "ongoing", "completed", "archived"];
var TripStatus = z.enum(TRIP_STATUSES);
var PLACE_KINDS = [
  "sight",
  "ruins",
  "nature",
  "beach",
  "viewpoint",
  "city",
  "lodging",
  "food",
  "activity",
  "transport",
  "airport",
  "shopping",
  "service",
  "other"
];
var PlaceKind = z.enum(PLACE_KINDS);
var ACTIVITY_KINDS = [
  "note",
  "visit",
  "meal",
  "transfer",
  "checkin",
  "checkout",
  "flight",
  "drive",
  "walk",
  "boat",
  "tour",
  "rest",
  "shopping",
  "other"
];
var ActivityKind = z.enum(ACTIVITY_KINDS);
var RESERVATION_KINDS = [
  "flight",
  "lodging",
  "car",
  "train",
  "bus",
  "ferry",
  "tour",
  "insurance",
  "other"
];
var ReservationKind = z.enum(RESERVATION_KINDS);
var RESERVATION_STATUSES = ["booked", "planned"];
var ReservationStatus = z.enum(RESERVATION_STATUSES);
var PHOTO_VISIBILITIES = ["private", "trip", "public"];
var PhotoVisibility = z.enum(PHOTO_VISIBILITIES);
var DOCUMENT_GROUPS = [
  "tickets",
  "lodging",
  "car",
  "insurance",
  "notes",
  "other"
];
var DocumentGroup = z.enum(DOCUMENT_GROUPS);
var AI_JOB_KINDS = [
  "plan_trip",
  "fill_days",
  "suggest_places",
  "suggest_food",
  "enrich_place",
  "edit_trip"
];
var AiJobKind = z.enum(AI_JOB_KINDS);
var AI_JOB_STATUSES = ["queued", "running", "succeeded", "failed", "cancelled"];
var AiJobStatus = z.enum(AI_JOB_STATUSES);
var TRIP_PACES = ["relaxed", "balanced", "packed"];
var TripPace = z.enum(TRIP_PACES);
var TRAVEL_STYLES = [
  "nature",
  "culture",
  "beach",
  "food",
  "nightlife",
  "active",
  "roadtrip",
  "family",
  "romantic",
  "budget",
  "luxury"
];
var TravelStyle = z.enum(TRAVEL_STYLES);

// src/ids.ts
var HEX = "0123456789abcdef";
function randomBytes(length) {
  const bytes = new Uint8Array(length);
  const cryptoObj = globalThis.crypto;
  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(bytes);
    return bytes;
  }
  for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return bytes;
}
function toHex(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i];
    out += `${HEX[b >> 4] ?? "0"}${HEX[b & 15] ?? "0"}`;
  }
  return out;
}
function uuidv7(now = Date.now()) {
  const bytes = randomBytes(16);
  const ts = BigInt(now);
  bytes[0] = Number(ts >> 40n & 0xffn);
  bytes[1] = Number(ts >> 32n & 0xffn);
  bytes[2] = Number(ts >> 24n & 0xffn);
  bytes[3] = Number(ts >> 16n & 0xffn);
  bytes[4] = Number(ts >> 8n & 0xffn);
  bytes[5] = Number(ts & 0xffn);
  bytes[6] = bytes[6] & 15 | 112;
  bytes[8] = bytes[8] & 63 | 128;
  const hex = toHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function randomToken(length = 24) {
  const alphabet = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
  const bytes = randomBytes(length);
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += alphabet[bytes[i] % alphabet.length];
  }
  return out;
}
var TRANSLIT = {
  \u0430: "a",
  \u0431: "b",
  \u0432: "v",
  \u0433: "g",
  \u0434: "d",
  \u0435: "e",
  \u0451: "e",
  \u0436: "zh",
  \u0437: "z",
  \u0438: "i",
  \u0439: "y",
  \u043A: "k",
  \u043B: "l",
  \u043C: "m",
  \u043D: "n",
  \u043E: "o",
  \u043F: "p",
  \u0440: "r",
  \u0441: "s",
  \u0442: "t",
  \u0443: "u",
  \u0444: "f",
  \u0445: "h",
  \u0446: "c",
  \u0447: "ch",
  \u0448: "sh",
  \u0449: "sch",
  \u044A: "",
  \u044B: "y",
  \u044C: "",
  \u044D: "e",
  \u044E: "yu",
  \u044F: "ya"
};
function slugify(input, maxLength = 60) {
  const lower = input.toLowerCase().trim();
  let out = "";
  for (const ch of lower) {
    if (TRANSLIT[ch] !== void 0) out += TRANSLIT[ch];
    else if (/[a-z0-9]/.test(ch)) out += ch;
    else out += "-";
  }
  return out.replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, maxLength).replace(/-$/, "") || "trip";
}

// src/geo.ts
var EARTH_RADIUS_M = 63710088e-1;
var toRad = (deg) => deg * Math.PI / 180;
function haversineMeters(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
function boundsOf(points) {
  if (!points.length) return null;
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;
  for (const p of points) {
    if (p.lat > north) north = p.lat;
    if (p.lat < south) south = p.lat;
    if (p.lng > east) east = p.lng;
    if (p.lng < west) west = p.lng;
  }
  return { north, south, east, west };
}
function centroidOf(points) {
  if (!points.length) return null;
  let lat = 0;
  let lng = 0;
  for (const p of points) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / points.length, lng: lng / points.length };
}
function nearest(point, candidates, maxMeters = Number.POSITIVE_INFINITY) {
  let best = null;
  for (const candidate of candidates) {
    if (candidate.lat === null || candidate.lng === null) continue;
    const distance = haversineMeters(point, { lat: candidate.lat, lng: candidate.lng });
    if (distance > maxMeters) continue;
    if (!best || distance < best.distance) best = { item: candidate, distance };
  }
  return best;
}
function estimateDriveMinutes(a, b, averageKmh = 55) {
  const straightKm = haversineMeters(a, b) / 1e3;
  const roadKm = straightKm * 1.35;
  return Math.round(roadKm / averageKmh * 60);
}

// src/booking.ts
var enc = encodeURIComponent;
var LOCAL_ONLY = /* @__PURE__ */ new Set(["RU", "BY"]);
function lodgingLinks(query) {
  const country = query.country.trim().toUpperCase();
  const city = query.city.trim();
  const name = query.name?.trim();
  const term = name ? `${name} ${city}` : city;
  const dates = query.checkIn && query.checkOut ? { in: query.checkIn, out: query.checkOut } : null;
  if (LOCAL_ONLY.has(country)) {
    const links = [
      {
        label: "\u042F\u043D\u0434\u0435\u043A\u0441 \u041F\u0443\u0442\u0435\u0448\u0435\u0441\u0442\u0432\u0438\u044F",
        url: `https://travel.yandex.ru/hotels/search/?text=${enc(term)}` + (dates ? `&checkinDate=${dates.in}&checkoutDate=${dates.out}&adults=2` : "")
      },
      {
        label: "\u041E\u0441\u0442\u0440\u043E\u0432\u043E\u043A",
        url: `https://ostrovok.ru/hotel/search/?q=${enc(term)}` + (dates ? `&dates=${dates.in}-${dates.out}` : "")
      },
      {
        label: "\u0421\u0443\u0442\u043E\u0447\u043D\u043E.\u0440\u0443",
        url: `https://sutochno.ru/search?occupied=${enc(city)}${dates ? `&d_from=${dates.in}&d_to=${dates.out}` : ""}`
      }
    ];
    return links;
  }
  return [
    {
      label: "Booking.com",
      url: `https://www.booking.com/searchresults.html?ss=${enc(term)}` + (dates ? `&checkin=${dates.in}&checkout=${dates.out}&group_adults=2` : "")
    },
    {
      label: "Airbnb",
      url: `https://www.airbnb.com/s/${enc(city)}/homes` + (dates ? `?checkin=${dates.in}&checkout=${dates.out}` : "")
    },
    {
      label: "Google Hotels",
      url: `https://www.google.com/travel/search?q=${enc(term)}`
    }
  ];
}
function mapsSearchLink(query, country) {
  return LOCAL_ONLY.has(country.trim().toUpperCase()) ? { label: "\u042F\u043D\u0434\u0435\u043A\u0441 \u041A\u0430\u0440\u0442\u044B", url: `https://yandex.ru/maps/?text=${enc(query)}` } : { label: "Google Maps", url: `https://www.google.com/maps/search/${enc(query)}` };
}
function estimateFlightCost(distanceKm, travellers = 1, currency = "USD") {
  const km = Math.max(0, distanceKm);
  const perKm = km < 800 ? 0.18 : km < 3e3 ? 0.11 : 0.07;
  const usd = (60 + km * perKm) * Math.max(1, travellers);
  return fromUsd(usd, currency);
}
var USD_RATES = {
  USD: 1,
  EUR: 0.92,
  RUB: 92,
  TRY: 39,
  GEL: 2.7,
  AED: 3.67,
  THB: 35,
  RSD: 108,
  GBP: 0.79,
  KZT: 480,
  UAH: 41
};
function roundEstimate(amount) {
  if (amount >= 1e4) return Math.round(amount / 500) * 500;
  if (amount >= 1e3) return Math.round(amount / 100) * 100;
  if (amount >= 100) return Math.round(amount / 10) * 10;
  return Math.round(amount);
}
function fromUsd(usd, currency) {
  const code = currency.trim().toUpperCase();
  const rate = USD_RATES[code];
  if (!rate) return { amount: roundEstimate(usd), currency: "USD" };
  return { amount: roundEstimate(usd * rate), currency: code };
}
function canEstimateIn(currency) {
  return Boolean(USD_RATES[currency.trim().toUpperCase()]);
}
function travelLinks(query) {
  const from = query.fromCity.trim();
  const to = query.toCity.trim();
  if (!from || !to) return [];
  const when = query.date ? `&when=${query.date}` : "";
  const local = LOCAL_ONLY.has(query.fromCountry.trim().toUpperCase());
  if (local) {
    return [
      {
        label: "\u042F\u043D\u0434\u0435\u043A\u0441 \u0420\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044F",
        url: `https://rasp.yandex.ru/search/?fromName=${enc(from)}&toName=${enc(to)}${when}`
      },
      {
        label: "\u0422\u0443\u0442\u0443.\u0440\u0443",
        url: `https://www.tutu.ru/poezda/rezult/?nnst1=${enc(from)}&nnst2=${enc(to)}`
      },
      {
        label: "\u0410\u0432\u0438\u0430\u0441\u0435\u0439\u043B\u0441",
        url: `https://www.aviasales.ru/search?origin_name=${enc(from)}&destination_name=${enc(to)}`
      }
    ];
  }
  return [
    {
      label: "Google Flights",
      url: `https://www.google.com/travel/flights?q=${enc(`flights from ${from} to ${to}`)}`
    },
    { label: "Skyscanner", url: `https://www.skyscanner.net/transport/flights/` },
    { label: "Omio (\u043F\u043E\u0435\u0437\u0434\u0430 \u0438 \u0430\u0432\u0442\u043E\u0431\u0443\u0441\u044B)", url: `https://www.omio.com/` }
  ];
}
function driveEstimate(distanceKm) {
  if (!Number.isFinite(distanceKm) || distanceKm < 30) return null;
  const hours = distanceKm * 1.25 / 75;
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  const label = whole > 0 ? `~${Math.round(distanceKm * 1.25)} \u043A\u043C \xB7 \u043F\u0440\u0438\u043C\u0435\u0440\u043D\u043E ${whole} \u0447${minutes ? ` ${minutes} \u043C\u0438\u043D` : ""} \u0437\u0430 \u0440\u0443\u043B\u0451\u043C` : `~${Math.round(distanceKm * 1.25)} \u043A\u043C \xB7 \u043F\u0440\u0438\u043C\u0435\u0440\u043D\u043E ${minutes} \u043C\u0438\u043D \u0437\u0430 \u0440\u0443\u043B\u0451\u043C`;
  return { hours, label };
}

// src/budget.ts
var BUDGET_CATEGORIES = [
  "flights",
  "car",
  "transport",
  "lodging",
  "food",
  "activities",
  "other"
];
var RESERVATION_CATEGORY = {
  flight: "flights",
  train: "transport",
  bus: "transport",
  ferry: "transport",
  car: "car",
  lodging: "lodging",
  tour: "activities",
  insurance: "other",
  other: "other"
};
var ACTIVITY_CATEGORY = {
  meal: "food",
  drive: "transport",
  transfer: "transport",
  flight: "flights",
  boat: "transport",
  checkin: "lodging",
  checkout: "lodging",
  tour: "activities",
  visit: "activities",
  walk: "activities",
  shopping: "other",
  rest: "other",
  note: "other",
  other: "other"
};
var PLACE_CATEGORY = {
  lodging: "lodging",
  food: "food",
  transport: "transport",
  airport: "transport",
  shopping: "other",
  service: "other"
};
function categoriseReservation(kind) {
  return RESERVATION_CATEGORY[kind] ?? "other";
}
function categoriseActivity(kind) {
  return ACTIVITY_CATEGORY[kind] ?? "activities";
}
function categorisePlace(kind) {
  return PLACE_CATEGORY[kind] ?? "activities";
}
var NAME_TITLES = /* @__PURE__ */ new Set(["MR", "MRS", "MS", "MISS", "MSTR", "DR", "\u0413", "\u0413-\u041D", "\u0413-\u0416\u0410"]);
function nameWords(name) {
  return name.toUpperCase().replace(/Ё/g, "\u0415").split(/[^\p{L}-]+/u).filter((word) => word && !NAME_TITLES.has(word));
}
function uniquePassengers(names) {
  const kept = [];
  for (const raw of names) {
    const name = raw?.trim();
    if (!name) continue;
    const words = new Set(nameWords(name));
    if (!words.size) continue;
    const covers = (a, b) => [...b].every((word) => a.has(word));
    const same = kept.find((k) => covers(k.words, words) || covers(words, k.words));
    if (!same) kept.push({ name, words });
    else if (words.size > same.words.size || words.size === same.words.size && !/\//.test(name) && /\//.test(same.name)) {
      same.name = name;
      same.words = words;
    }
  }
  return kept.map((k) => k.name);
}
function partySize(input) {
  const ticketed = Math.max(0, ...input.passengers.map((list) => uniquePassengers(list).length));
  const n = ticketed > 0 ? ticketed : Math.max(input.travelerCount, input.editors, 1);
  return Math.min(50, Math.max(1, n));
}
function convertMoney(money, target, rates) {
  const from = money.currency.toUpperCase();
  const to = target.toUpperCase();
  if (from === to) return { amount: money.amount, currency: to };
  if (!rates) return null;
  const base = rates.base.toUpperCase();
  const perBase = (code) => {
    if (code === base) return 1;
    const rate = rates.rates[code] ?? rates.rates[code.toLowerCase()];
    return typeof rate === "number" && Number.isFinite(rate) && rate > 0 ? rate : null;
  };
  const fromRate = perBase(from);
  const toRate = perBase(to);
  if (fromRate === null || toRate === null) return null;
  return { amount: round(money.amount / fromRate * toRate), currency: to };
}
var Bucket = class {
  constructor(target, rates) {
    this.target = target;
    this.rates = rates;
  }
  target;
  rates;
  byCurrency = /* @__PURE__ */ new Map();
  count = 0;
  add(money, free = false) {
    if (!money || !Number.isFinite(money.amount)) return false;
    if (money.amount === 0) {
      if (!free) return false;
      this.count += 1;
      return true;
    }
    const converted = this.rates ? convertMoney(money, this.target, this.rates) ?? money : money;
    const currency = converted.currency.toUpperCase();
    this.byCurrency.set(currency, (this.byCurrency.get(currency) ?? 0) + converted.amount);
    this.count += 1;
    return true;
  }
  totals(preferred) {
    const upper = preferred.toUpperCase();
    return [...this.byCurrency.entries()].map(([currency, amount]) => ({ currency, amount: round(amount) })).sort((a, b) => {
      if (a.currency === upper) return -1;
      if (b.currency === upper) return 1;
      return b.amount - a.amount;
    });
  }
};
var round = (value) => Math.round(value * 100) / 100;
var DAILY_SPEND_USD = {
  budget: 35,
  mid: 70,
  premium: 150
};
function estimateDailySpend(budgetLevel, travellers, currency = "USD") {
  const perPerson = DAILY_SPEND_USD[budgetLevel ?? "mid"];
  return fromUsd(perPerson * Math.max(1, travellers), currency);
}
var ITEM_COST_USD = {
  sight: { budget: 5, mid: 10, premium: 20 },
  ruins: { budget: 5, mid: 10, premium: 20 },
  viewpoint: { budget: 0, mid: 3, premium: 8 },
  nature: { budget: 0, mid: 5, premium: 12 },
  beach: { budget: 0, mid: 0, premium: 5 },
  food: { budget: 8, mid: 18, premium: 40 },
  activity: { budget: 15, mid: 35, premium: 80 }
};
function estimateItemCost(kind, budgetLevel, currency = "USD") {
  const tiers = ITEM_COST_USD[kind];
  if (!tiers) return null;
  return fromUsd(tiers[budgetLevel ?? "mid"], currency);
}
var UNPRICED_ACTIVITY = /* @__PURE__ */ new Set(["note", "rest", "checkin", "checkout", "flight", "drive", "transfer", "walk"]);
var UNPRICED_PLACE = /* @__PURE__ */ new Set(["city", "lodging", "transport", "airport"]);
var times = (money, n) => money ? { amount: round(money.amount * n), currency: money.currency } : null;
function summariseBudget(input) {
  const rates = input.rates ?? null;
  const party = Math.max(1, Math.round(input.party ?? 1));
  const grand = new Bucket(input.currency, rates);
  const optional = new Bucket(input.currency, rates);
  const categories = /* @__PURE__ */ new Map();
  const days = /* @__PURE__ */ new Map();
  let missing = 0;
  const bucketFor = (map, key) => {
    const existing = map.get(key);
    if (existing) return existing;
    const created = new Bucket(input.currency, rates);
    map.set(key, created);
    return created;
  };
  const record = (money, category, dayIndex, free = false) => {
    if (!grand.add(money, free)) {
      missing += 1;
      return;
    }
    bucketFor(categories, category).add(money, free);
    if (dayIndex !== null) bucketFor(days, dayIndex).add(money, free);
  };
  const dayIndexById = new Map(input.days.map((d) => [d.id, d.dayIndex]));
  const placeById = new Map(input.places.flatMap((p) => p.id ? [[p.id, p]] : []));
  const visited = /* @__PURE__ */ new Set();
  for (const reservation of input.reservations) {
    record(
      reservation.price,
      categoriseReservation(reservation.kind),
      reservation.dayId ? dayIndexById.get(reservation.dayId) ?? null : null
    );
  }
  for (const day of input.days) {
    for (const activity of day.activities) {
      const place = activity.placeId ? placeById.get(activity.placeId) : void 0;
      if (place) visited.add(place.id);
      const perHead = activity.cost ?? (place && place.kind !== "lodging" ? place.cost : null);
      const free = !activity.cost && Boolean(place?.free);
      const category = activity.cost || !place ? categoriseActivity(activity.kind) : categorisePlace(place.kind);
      const priceable = Boolean(perHead) || free || !UNPRICED_ACTIVITY.has(activity.kind) && !(place && UNPRICED_PLACE.has(place.kind));
      if (!priceable) continue;
      if (activity.optional) {
        optional.add(times(perHead, party), free);
        continue;
      }
      record(times(perHead, party), category, day.dayIndex, free);
    }
  }
  let unscheduled = 0;
  for (const place of input.places) {
    if (place.kind === "lodging") {
      if (place.cost) record(place.cost, "lodging", null);
      continue;
    }
    if (place.id === void 0) {
      record(times(place.cost, party), categorisePlace(place.kind), null, place.free);
      continue;
    }
    if (!visited.has(place.id) && (place.cost || place.free)) unscheduled += 1;
  }
  const totals = grand.totals(input.currency);
  const planned = input.budget;
  const mainTotal = planned ? totals.find((t) => t.currency === planned.currency.toUpperCase()) : void 0;
  return {
    totals,
    byCategory: BUDGET_CATEGORIES.filter((c) => categories.has(c)).map((category) => {
      const bucket = categories.get(category);
      return { category, totals: bucket.totals(input.currency), count: bucket.count };
    }),
    byDay: [...days.entries()].sort((a, b) => a[0] - b[0]).map(([dayIndex, bucket]) => ({
      dayIndex,
      totals: bucket.totals(input.currency),
      count: bucket.count
    })),
    counted: grand.count,
    missing,
    planned,
    // Only meaningful when the plan and the spend are in the same currency.
    remainder: planned && mainTotal ? { amount: round(planned.amount - mainTotal.amount), currency: planned.currency } : null,
    party,
    optional: { totals: optional.totals(input.currency), count: optional.count },
    unscheduled
  };
}

// src/offline.ts
function tripMediaUrls(trip, origin, { fullPhotos = true } = {}) {
  const thumbs = /* @__PURE__ */ new Set();
  const full = /* @__PURE__ */ new Set();
  const usable = (url) => {
    if (!url) return false;
    if (url.startsWith("/")) return true;
    if (!/^https?:\/\//i.test(url)) return false;
    if (!origin) return false;
    return url.startsWith(origin.replace(/\/$/, ""));
  };
  for (const photo of trip.photos ?? []) {
    if (usable(photo.thumbUrl)) thumbs.add(photo.thumbUrl);
    if (fullPhotos && usable(photo.url)) full.add(photo.url);
  }
  for (const place of trip.places ?? []) {
    if (usable(place.coverPhotoUrl)) thumbs.add(place.coverPhotoUrl);
  }
  if (usable(trip.coverUrl)) thumbs.add(trip.coverUrl);
  for (const doc of trip.documents ?? []) {
    if (usable(doc.url)) full.add(doc.url);
  }
  for (const url of thumbs) full.delete(url);
  return [...thumbs, ...full];
}

// src/gpx.ts
var SYMBOLS = {
  lodging: "Lodging",
  food: "Restaurant",
  beach: "Beach",
  nature: "Park",
  viewpoint: "Scenic Area",
  sight: "Museum",
  ruins: "Museum",
  city: "City",
  airport: "Airport",
  transport: "Car",
  shopping: "Shopping Center",
  activity: "Flag, Blue"
};
var gpxSymbolFor = (kind) => SYMBOLS[kind] ?? "Flag, Blue";
function escapeXml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;").replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");
}
var coord = (value) => value.toFixed(6);
var isFinitePair = (lat, lng) => typeof lat === "number" && typeof lng === "number" && Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
function buildGpx(doc) {
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="Travessa" xmlns="http://www.topografix.com/GPX/1/1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">',
    "  <metadata>",
    `    <name>${escapeXml(doc.name)}</name>`
  ];
  if (doc.description) lines.push(`    <desc>${escapeXml(doc.description)}</desc>`);
  lines.push(`    <time>${doc.time ?? (/* @__PURE__ */ new Date()).toISOString()}</time>`);
  lines.push("  </metadata>");
  for (const point of doc.waypoints) {
    if (!isFinitePair(point.lat, point.lng)) continue;
    lines.push(`  <wpt lat="${coord(point.lat)}" lon="${coord(point.lng)}">`);
    lines.push(`    <name>${escapeXml(point.name)}</name>`);
    if (point.description) lines.push(`    <desc>${escapeXml(point.description)}</desc>`);
    if (point.symbol) lines.push(`    <sym>${escapeXml(point.symbol)}</sym>`);
    if (point.dayIndex) lines.push(`    <type>\u0414\u0435\u043D\u044C ${point.dayIndex}</type>`);
    lines.push("  </wpt>");
  }
  for (const route of doc.routes ?? []) {
    const points = route.points.filter((p) => isFinitePair(p.lat, p.lng));
    if (points.length < 2) continue;
    lines.push("  <rte>");
    lines.push(`    <name>${escapeXml(route.name)}</name>`);
    for (const point of points) {
      lines.push(`    <rtept lat="${coord(point.lat)}" lon="${coord(point.lng)}">`);
      lines.push(`      <name>${escapeXml(point.name)}</name>`);
      lines.push("    </rtept>");
    }
    lines.push("  </rte>");
  }
  lines.push("</gpx>");
  return lines.join("\n");
}
function gpxFilename(title) {
  const slug = title.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 60);
  return `${slug || "travessa"}.gpx`;
}

// src/plan-fact.ts
function summarisePlanFact(input) {
  const photosByPlace = /* @__PURE__ */ new Map();
  const photosByDay = /* @__PURE__ */ new Map();
  for (const photo of input.photos) {
    if (photo.placeId) photosByPlace.set(photo.placeId, (photosByPlace.get(photo.placeId) ?? 0) + 1);
    if (photo.dayId) photosByDay.set(photo.dayId, (photosByDay.get(photo.dayId) ?? 0) + 1);
  }
  const anyTicked = input.days.some((day) => day.activities.some((a) => a.done));
  const anyPhotos = input.photos.length > 0;
  const noEvidence = !anyTicked && !anyPhotos;
  const visitedPlaces = /* @__PURE__ */ new Set();
  let planned = 0;
  let done = 0;
  let skipped = 0;
  let unknown = 0;
  const days = input.days.map((day) => {
    const items = day.activities.map((activity) => {
      const hasPhoto = activity.placeId ? (photosByPlace.get(activity.placeId) ?? 0) > 0 : false;
      let outcome;
      if (activity.done || hasPhoto) {
        outcome = "done";
        if (activity.placeId) visitedPlaces.add(activity.placeId);
      } else if (noEvidence) {
        outcome = "unknown";
      } else {
        outcome = "skipped";
      }
      return {
        id: activity.id,
        title: activity.title,
        outcome,
        optional: activity.isOptional ?? false
      };
    });
    const dayDone = items.filter((i) => i.outcome === "done").length;
    const daySkipped = items.filter((i) => i.outcome === "skipped").length;
    const dayUnknown = items.filter((i) => i.outcome === "unknown").length;
    planned += items.length;
    done += dayDone;
    skipped += daySkipped;
    unknown += dayUnknown;
    return {
      dayIndex: day.dayIndex,
      title: day.title ?? null,
      planned: items.length,
      done: dayDone,
      skipped: daySkipped,
      unknown: dayUnknown,
      photoCount: photosByDay.get(day.id) ?? 0,
      items
    };
  });
  for (const [placeId, count] of photosByPlace) {
    if (count > 0) visitedPlaces.add(placeId);
  }
  return {
    days,
    planned,
    done,
    skipped,
    unknown,
    unvisitedPlaces: input.places.filter((place) => !visitedPlaces.has(place.id)).map((place) => place.name),
    completion: planned > 0 && !noEvidence ? Math.round(done / planned * 100) / 100 : null,
    noEvidence
  };
}

// src/day-order.ts
function chronologicalIndex(items, startTime) {
  if (!startTime) return items.length;
  let index = 0;
  let effective = "";
  items.forEach((item, i) => {
    if (item.startTime) effective = item.startTime;
    if (effective <= startTime) index = i + 1;
  });
  return index;
}
function sortOrderAt(items, index) {
  const prev = items[index - 1]?.sortOrder;
  const next = items[index]?.sortOrder;
  if (next === void 0) return { sortOrder: (prev ?? 0) + 10, renumber: [] };
  if (prev === void 0) return { sortOrder: next - 10, renumber: [] };
  if (next - prev >= 2) return { sortOrder: Math.floor((prev + next) / 2), renumber: [] };
  const renumber = [];
  items.forEach((item, i) => {
    const sortOrder = (i < index ? i + 1 : i + 2) * 10;
    if (sortOrder !== item.sortOrder) renumber.push({ id: item.id, sortOrder });
  });
  return { sortOrder: (index + 1) * 10, renumber };
}
function timeForSlot(previous, next, current, ownDuration) {
  const prevStart = previous?.startTime ?? null;
  const nextStart = next?.startTime ?? null;
  const fits = (value) => (prevStart === null || value >= prevStart) && (nextStart === null || value <= nextStart);
  if (current && fits(current)) return current;
  const prevSpan = Math.max(previous?.durationMinutes ?? 0, 0);
  const afterPrevious = prevStart ? shiftMinutes(prevStart, prevSpan > 0 ? prevSpan : 15) : null;
  if (afterPrevious && fits(afterPrevious)) return afterPrevious;
  if (prevStart && fits(prevStart)) return prevStart;
  const beforeNext = nextStart ? shiftMinutes(nextStart, -Math.max(ownDuration ?? 0, 15)) : null;
  if (beforeNext && fits(beforeNext)) return beforeNext;
  if (nextStart && fits(nextStart)) return nextStart;
  return current;
}
function timesAfterReorder(ordered, movedIds) {
  const moved = new Set(movedIds);
  const out = [];
  const timeOf2 = new Map(ordered.map((item) => [item.id, item.startTime]));
  const at = (id) => timeOf2.get(id) ?? null;
  ordered.forEach((item, index) => {
    if (!moved.has(item.id)) return;
    const previous = [...ordered.slice(0, index)].reverse().find((row) => at(row.id));
    const next = ordered.slice(index + 1).find((row) => at(row.id));
    const prevTime = previous ? at(previous.id) : null;
    const nextTime = next ? at(next.id) : null;
    const time = timeForSlot(
      prevTime ? { startTime: prevTime, durationMinutes: previous?.durationMinutes } : null,
      nextTime ? { startTime: nextTime } : null,
      at(item.id),
      item.durationMinutes
    );
    if (time && time !== item.startTime) {
      out.push({ id: item.id, startTime: time });
      timeOf2.set(item.id, time);
    }
  });
  return out;
}
function shiftMinutes(value, minutes) {
  const [hours, mins] = value.split(":").map((part) => Number(part));
  const total = Math.min(23 * 60 + 59, Math.max(0, (hours ?? 0) * 60 + (mins ?? 0) + minutes));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}
function placeByTime(items, startTime) {
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  return sortOrderAt(sorted, chronologicalIndex(sorted, startTime));
}
function fillVisibleSlots(full, wasVisible, visibleOrder) {
  const out = [];
  let next = 0;
  for (const id of full) {
    if (!wasVisible.has(id)) out.push(id);
    else if (next < visibleOrder.length) out.push(visibleOrder[next++]);
  }
  while (next < visibleOrder.length) out.push(visibleOrder[next++]);
  return out;
}

// src/projection.ts
var MONTHS_RU = [
  "\u044F\u043D\u0432\u0430\u0440\u044F",
  "\u0444\u0435\u0432\u0440\u0430\u043B\u044F",
  "\u043C\u0430\u0440\u0442\u0430",
  "\u0430\u043F\u0440\u0435\u043B\u044F",
  "\u043C\u0430\u044F",
  "\u0438\u044E\u043D\u044F",
  "\u0438\u044E\u043B\u044F",
  "\u0430\u0432\u0433\u0443\u0441\u0442\u0430",
  "\u0441\u0435\u043D\u0442\u044F\u0431\u0440\u044F",
  "\u043E\u043A\u0442\u044F\u0431\u0440\u044F",
  "\u043D\u043E\u044F\u0431\u0440\u044F",
  "\u0434\u0435\u043A\u0430\u0431\u0440\u044F"
];
var MONTHS_RU_NOM = [
  "\u044F\u043D\u0432\u0430\u0440\u044C",
  "\u0444\u0435\u0432\u0440\u0430\u043B\u044C",
  "\u043C\u0430\u0440\u0442",
  "\u0430\u043F\u0440\u0435\u043B\u044C",
  "\u043C\u0430\u0439",
  "\u0438\u044E\u043D\u044C",
  "\u0438\u044E\u043B\u044C",
  "\u0430\u0432\u0433\u0443\u0441\u0442",
  "\u0441\u0435\u043D\u0442\u044F\u0431\u0440\u044C",
  "\u043E\u043A\u0442\u044F\u0431\u0440\u044C",
  "\u043D\u043E\u044F\u0431\u0440\u044C",
  "\u0434\u0435\u043A\u0430\u0431\u0440\u044C"
];
var MONTHS_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
function parseIsoDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
}
function pluralRu(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
function formatTripDateLabel(startDate, endDate, nights, showExact, lang = "ru") {
  const start = startDate ? parseIsoDate(startDate) : null;
  const end = endDate ? parseIsoDate(endDate) : null;
  if (showExact && start) {
    if (lang === "en") {
      const sm2 = MONTHS_EN[start.m] ?? "";
      if (!end) return `${sm2} ${start.d}, ${start.y}`;
      const em2 = MONTHS_EN[end.m] ?? "";
      return start.m === end.m && start.y === end.y ? `${sm2} ${start.d}\u2013${end.d}, ${start.y}` : `${sm2} ${start.d} \u2013 ${em2} ${end.d}, ${end.y}`;
    }
    const sm = MONTHS_RU[start.m] ?? "";
    if (!end) return `${start.d} ${sm} ${start.y}`;
    const em = MONTHS_RU[end.m] ?? "";
    return start.m === end.m && start.y === end.y ? `${start.d}\u2013${end.d} ${sm} ${start.y}` : `${start.d} ${sm} \u2013 ${end.d} ${em} ${end.y}`;
  }
  const parts = [];
  if (start) {
    parts.push(
      lang === "en" ? `${MONTHS_EN[start.m] ?? ""} ${start.y}` : `${MONTHS_RU_NOM[start.m] ?? ""} ${start.y}`
    );
  }
  if (nights && nights > 0) {
    parts.push(
      lang === "en" ? `${nights} night${nights === 1 ? "" : "s"}` : `${nights} ${pluralRu(nights, "\u043D\u043E\u0447\u044C", "\u043D\u043E\u0447\u0438", "\u043D\u043E\u0447\u0435\u0439")}`
    );
  }
  return parts.length ? parts.join(" \xB7 ") : null;
}
function dayLabel(dayIndex, lang) {
  return lang === "en" ? `Day ${dayIndex}` : `\u0414\u0435\u043D\u044C ${dayIndex}`;
}
function projectActivity(activity, privacy) {
  return {
    id: activity.id,
    tripId: activity.tripId,
    dayId: activity.dayId,
    kind: activity.kind,
    title: activity.title,
    description: privacy.showPersonalNotes ? activity.description : truncateToPublic(activity.description),
    startTime: privacy.showExactDates ? activity.startTime : null,
    endTime: privacy.showExactDates ? activity.endTime : null,
    durationMinutes: activity.durationMinutes,
    placeId: activity.placeId,
    isOptional: activity.isOptional,
    sortOrder: activity.sortOrder
  };
}
var SENSITIVE_LINE = /(бронир|подтвержд|confirmation|booking|PNR|депозит|deposit|пароль|пин[- ]?код|pin code|паспорт|passport|\+\d{7,}|\b\d{6,}\b)/i;
function truncateToPublic(text) {
  if (!text) return null;
  const keptLines = text.split(/\n+/).map((line) => dropSensitiveSentences(line)).filter((line) => line.length > 0);
  const kept = keptLines.join("\n").trim();
  return kept.length ? kept : null;
}
function dropSensitiveSentences(line) {
  if (!SENSITIVE_LINE.test(line)) return line.trim();
  const sentences = line.match(/[^.!?…]+[.!?…]*\s*/g);
  if (!sentences) return "";
  return sentences.filter((sentence) => !SENSITIVE_LINE.test(sentence)).join("").trim();
}
function projectPlace(place, privacy) {
  const isLodging = place.kind === "lodging";
  return {
    id: place.id,
    tripId: place.tripId,
    name: place.name,
    kind: place.kind,
    lat: privacy.showMap ? place.lat : null,
    lng: privacy.showMap ? place.lng : null,
    // A hotel's street address is close enough to "where these people slept".
    address: isLodging ? null : place.address,
    description: truncateToPublic(place.description),
    highlight: place.highlight,
    optional: place.optional,
    tags: place.tags,
    ticket: privacy.showPrices ? place.ticket : stripTicketPrice(place.ticket),
    links: place.links.filter((l) => l.kind !== "booking"),
    // What anyone at the gate would be told: about the place, never the traveller.
    // A hotel's are left out with its address.
    caveats: isLodging ? [] : place.caveats,
    hours: isLodging ? null : place.hours,
    durationMinutes: place.durationMinutes,
    rating: place.rating,
    sortOrder: place.sortOrder,
    photoCount: privacy.showPhotos ? place.photoCount : 0,
    coverPhotoUrl: privacy.showPhotos ? place.coverPhotoUrl : null,
    updatedAt: place.updatedAt
  };
}
function stripTicketPrice(ticket) {
  if (!ticket) return null;
  return ticket;
}
function projectDay(day, privacy, lang) {
  return {
    id: day.id,
    tripId: day.tripId,
    dayIndex: day.dayIndex,
    date: privacy.showExactDates ? day.date : null,
    dateLabel: privacy.showExactDates ? day.date : dayLabel(day.dayIndex, lang),
    title: day.title,
    summary: truncateToPublic(day.summary),
    travelSummary: day.travelSummary,
    basePlaceId: privacy.showLodging ? day.basePlaceId : null,
    color: day.color,
    activities: day.activities.filter((a) => !a.isPrivate && !a.deletedAt).sort((a, b) => a.sortOrder - b.sortOrder).map((a) => projectActivity(a, privacy)),
    placeIds: day.placeIds
  };
}
function projectReservation(reservation, privacy, lang) {
  return {
    id: reservation.id,
    kind: reservation.kind,
    title: reservation.title,
    provider: reservation.provider,
    fromLabel: reservation.fromLabel,
    toLabel: reservation.toLabel,
    placeId: reservation.placeId,
    details: truncateToPublic(reservation.details),
    dateLabel: privacy.showExactDates ? formatTripDateLabel(reservation.startDate, reservation.endDate, null, true, lang) : null,
    bookingUrl: reservation.bookingUrl
  };
}
function projectPhoto(photo) {
  return {
    id: photo.id,
    placeId: photo.placeId,
    dayId: photo.dayId,
    url: photo.url,
    thumbUrl: photo.thumbUrl,
    width: photo.width,
    height: photo.height,
    caption: photo.caption,
    sortOrder: photo.sortOrder
  };
}
function projectTrip(trip) {
  const privacy = trip.privacy;
  const lang = trip.language;
  const visiblePlaces = trip.places.filter((p) => !p.deletedAt);
  const lodgingIds = new Set(
    visiblePlaces.filter((p) => p.kind === "lodging").map((p) => p.id)
  );
  const foodIds = new Set(visiblePlaces.filter((p) => p.kind === "food").map((p) => p.id));
  const places = visiblePlaces.filter((p) => privacy.showLodging ? true : !lodgingIds.has(p.id)).filter((p) => privacy.showFood ? true : !foodIds.has(p.id)).map((p) => projectPlace(p, privacy));
  const keptPlaceIds = new Set(places.map((p) => p.id));
  const days = trip.days.filter((d) => !d.deletedAt).sort((a, b) => a.dayIndex - b.dayIndex).map((d) => projectDay(d, privacy, lang)).map((d) => ({
    ...d,
    placeIds: d.placeIds.filter((id) => keptPlaceIds.has(id)),
    activities: d.activities.filter((a) => !a.placeId || keptPlaceIds.has(a.placeId))
  }));
  const reservations = privacy.showTransport ? trip.reservations.filter((r) => !r.deletedAt).filter((r) => r.kind !== "lodging" || privacy.showLodging).sort((a, b) => a.sortOrder - b.sortOrder).map((r) => projectReservation(r, privacy, lang)) : [];
  const photos = privacy.showPhotos ? trip.photos.filter((p) => !p.deletedAt && p.visibility === "public").filter((p) => !p.placeId || keptPlaceIds.has(p.placeId)).sort((a, b) => a.sortOrder - b.sortOrder).map(projectPhoto) : [];
  return {
    id: trip.id,
    slug: trip.slug,
    title: trip.title,
    subtitle: trip.subtitle,
    summary: truncateToPublic(trip.summary),
    coverUrl: trip.coverUrl,
    accentColor: trip.accentColor,
    status: trip.status,
    nights: trip.nights,
    countries: trip.countries,
    regions: trip.regions,
    travelerCount: trip.travelerCount,
    pace: trip.pace,
    styles: trip.styles,
    tags: trip.tags,
    language: trip.language,
    forkCount: trip.forkCount,
    viewCount: trip.viewCount,
    isFeatured: trip.isFeatured,
    // A "состоявшееся путешествие" reads as more trustworthy; the timestamp is
    // not sensitive. Preferences, citizenships and visaInfo are deliberately
    // absent — they have no public representation.
    completedAt: trip.completedAt,
    owner: trip.owner,
    dateLabel: formatTripDateLabel(
      trip.startDate,
      trip.endDate,
      trip.nights,
      privacy.showExactDates,
      lang
    ),
    startDate: privacy.showExactDates ? trip.startDate : null,
    endDate: privacy.showExactDates ? trip.endDate : null,
    travelers: privacy.showTravelers ? trip.travelers : [],
    days,
    places,
    reservations,
    photos,
    budget: privacy.showPrices ? trip.budget : null,
    allowForking: privacy.allowForking,
    showMap: privacy.showMap,
    updatedAt: trip.updatedAt
  };
}

// src/countries.ts
var ABKHAZIA = "AB";
var UNOFFICIAL = { [ABKHAZIA]: "\u0410\u0431\u0445\u0430\u0437\u0438\u044F" };
var regionNames;
function countryNameRu(code) {
  if (!code) return "";
  const upper = code.trim().toUpperCase();
  if (UNOFFICIAL[upper]) return UNOFFICIAL[upper];
  try {
    regionNames ??= new Intl.DisplayNames(["ru"], { type: "region" });
    const name = regionNames.of(upper);
    return name && name !== upper ? name : "";
  } catch {
    return "";
  }
}
function isUnofficialCountry(code) {
  return Boolean(code && UNOFFICIAL[code.trim().toUpperCase()]);
}

// src/deal-filters.ts
var NIGHTS_MIN = 1;
var NIGHTS_MAX = 21;
var STAR_FLOORS = [3, 4, 5];
var NO_DEAL_FILTERS = { kind: "all", fit: [], minStars: null, meals: [], nights: null, countries: [], abroad: false, text: "" };
var DEAL_FILTER_KEYS = ["kind", "fit", "minStars", "meals", "nights", "countries", "abroad", "text"];
function kindGroup(kind) {
  return kind === "hotel" ? "hotels" : kind === "flight" || kind === "train" ? "tickets" : "tours";
}
function inNights(nights, [from, to]) {
  return nights >= from && (to >= NIGHTS_MAX || nights <= to);
}
function nightsSpan(value) {
  if (!Array.isArray(value) || value.length !== 2) return null;
  const [from, to] = value.map(Number);
  if (!Number.isInteger(from) || !Number.isInteger(to) || from > to) return null;
  const span = [Math.max(from, NIGHTS_MIN), Math.min(to, NIGHTS_MAX)];
  return span[0] <= NIGHTS_MIN && span[1] >= NIGHTS_MAX ? null : span;
}
var DAY_MS = 24 * 60 * 60 * 1e3;
var nightsBetween = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
function dealNights(result, tripNights) {
  const { deal } = result;
  if (deal.nights) return deal.nights;
  if (deal.dateFrom && deal.dateTo && !deal.flexibleDates && deal.dateTo > deal.dateFrom) return nightsBetween(deal.dateFrom, deal.dateTo);
  return kindGroup(deal.kind) === "tickets" ? tripNights : null;
}
var fold = (text) => text.toLowerCase().replace(/ё/g, "\u0435");
function valueOf(result, key, tripNights, home) {
  const { deal } = result;
  const ticket = kindGroup(deal.kind) === "tickets";
  switch (key) {
    case "kind":
      return kindGroup(deal.kind);
    case "fit":
      return result.fit;
    case "minStars":
      return ticket ? void 0 : deal.stars;
    case "meals":
      return ticket ? void 0 : deal.meal;
    case "nights": {
      const nights = dealNights(result, tripNights);
      return nights === null ? null : Math.min(nights, NIGHTS_MAX);
    }
    case "countries":
      return deal.destinationCountry;
    case "abroad":
      return deal.destinationCountry ? deal.destinationCountry.toUpperCase() === home ? "home" : "abroad" : null;
  }
}
function verdict(result, key, filters, tripNights, home, haystack) {
  if (!isActive(filters, key)) return "yes";
  if (key === "text") {
    const words = fold(filters.text).trim().split(/\s+/);
    const text = haystack();
    return words.every((word) => text.includes(word)) ? "yes" : "no";
  }
  const value = valueOf(result, key, tripNights, home);
  if (value === void 0) return "no";
  if (value === null) return "unknown";
  switch (key) {
    case "kind":
      return value === filters.kind ? "yes" : "no";
    case "minStars":
      return value >= filters.minStars ? "yes" : "no";
    case "abroad":
      return value === "abroad" ? "yes" : "no";
    case "nights":
      return inNights(value, filters.nights) ? "yes" : "no";
    default:
      return filters[key].includes(value) ? "yes" : "no";
  }
}
function isActive(filters, key) {
  const value = filters[key];
  if (key === "kind") return value !== "all";
  if (key === "abroad") return value === true;
  if (key === "minStars" || key === "nights") return value !== null;
  if (key === "text") return value.trim().length > 0;
  return value.length > 0;
}
function activeFilters(filters) {
  return DEAL_FILTER_KEYS.filter((key) => isActive(filters, key));
}
function filterDeals(results, filters, tripNights, home = "RU") {
  const homeCode = home.toUpperCase();
  const active = activeFilters(filters);
  const rows = results.map((result) => {
    let text = null;
    const haystack = () => text ??= fold([result.deal.title, result.deal.destinationCity ?? "", result.deal.summary, countryNameRu(result.deal.destinationCountry)].join(" "));
    const verdicts = Object.fromEntries(DEAL_FILTER_KEYS.map((key) => [key, verdict(result, key, filters, tripNights, homeCode, haystack)]));
    return { result, verdicts };
  });
  const matched = [];
  const maybe = [];
  let hidden = 0;
  for (const { result, verdicts } of rows) {
    const failed = active.some((key) => verdicts[key] === "no");
    if (failed) {
      hidden += 1;
      continue;
    }
    const unknown = active.filter((key) => verdicts[key] === "unknown");
    if (unknown.length) maybe.push({ result, unknown });
    else matched.push(result);
  }
  const passesExcept = (verdicts, except) => active.every((key) => key === except || verdicts[key] !== "no");
  const relax = active.map((key) => ({ key, count: rows.filter((row) => passesExcept(row.verdicts, key)).length }));
  const counts = { kind: {}, fit: {}, minStars: {}, meals: {}, nights: {}, countries: {}, abroad: {}, unknown: {} };
  for (const key of ["kind", "fit", "minStars", "meals", "nights", "countries", "abroad"]) {
    const tally = counts[key];
    for (const row of rows) {
      if (!passesExcept(row.verdicts, key)) continue;
      const value = valueOf(row.result, key, tripNights, homeCode);
      if (value === void 0) continue;
      if (value === null) counts.unknown[key] = (counts.unknown[key] ?? 0) + 1;
      else tally[String(value)] = (tally[String(value)] ?? 0) + 1;
    }
  }
  return { matched, maybe, hidden, relax, counts };
}

// src/flight-status.ts
import { z as z2 } from "zod";
var AIRCRAFT = /^(A3\d{2}|A2\d{2}|B7\d{2}|E1\d{2}|E7\d|CR\d|AT\d|Q4\d{2}|SU9\d)$/;
function flightIdentOf(text) {
  if (!text) return null;
  const pattern = /(^|[^\p{L}\p{N}])([A-Z]{2}|[A-Z]\d|\d[A-Z])[\s-]?(\d{1,4})(?![\p{L}\p{N}])/gu;
  for (const match of text.matchAll(pattern)) {
    const airline = match[2];
    const digits = match[3];
    if (AIRCRAFT.test(`${airline}${digits}`)) continue;
    const before = text.slice(0, match.index ?? 0).toLowerCase();
    if (/(airbus|boeing|embraer|superjet|sukhoi|бонус|бронь|брони|pnr)\W*$/i.test(before)) continue;
    if (digits.length < 2 && !/рейс\W*$/i.test(before)) continue;
    const number = digits.replace(/^0+(?=\d)/, "");
    return { airline, number };
  }
  return null;
}
var formatFlightIdent = (ident) => `${ident.airline} ${ident.number}`;
function flightIdentOfReservation(reservation) {
  return flightIdentOf(reservation.title) ?? flightIdentOf(reservation.details ?? null) ?? flightIdentOf(reservation.provider ?? null);
}
var CITY_AIRPORT = {
  \u043A\u0430\u043B\u0438\u043D\u0438\u043D\u0433\u0440\u0430\u0434: "KGD",
  \u0441\u043E\u0447\u0438: "AER",
  \u0430\u0434\u043B\u0435\u0440: "AER",
  \u043A\u0430\u0437\u0430\u043D\u044C: "KZN",
  "\u0441\u0430\u043D\u043A\u0442-\u043F\u0435\u0442\u0435\u0440\u0431\u0443\u0440\u0433": "LED",
  \u043F\u0435\u0442\u0435\u0440\u0431\u0443\u0440\u0433: "LED",
  \u0435\u043A\u0430\u0442\u0435\u0440\u0438\u043D\u0431\u0443\u0440\u0433: "SVX",
  \u043D\u043E\u0432\u043E\u0441\u0438\u0431\u0438\u0440\u0441\u043A: "OVB",
  \u043A\u0440\u0430\u0441\u043D\u043E\u0434\u0430\u0440: "KRR",
  \u043C\u0438\u043D\u0435\u0440\u0430\u043B\u044C\u043D\u044B\u0435: "MRV",
  \u0441\u0430\u043C\u0430\u0440\u0430: "KUF",
  \u0443\u0444\u0430: "UFA",
  \u043F\u0435\u0440\u043C\u044C: "PEE",
  \u0447\u0435\u0431\u043E\u043A\u0441\u0430\u0440\u044B: "CSY",
  \u043A\u0430\u043B\u0443\u0433\u0430: "KLF",
  \u043C\u0443\u0440\u043C\u0430\u043D\u0441\u043A: "MMK",
  \u0432\u043B\u0430\u0434\u0438\u0432\u043E\u0441\u0442\u043E\u043A: "VVO",
  \u0438\u0440\u043A\u0443\u0442\u0441\u043A: "IKT",
  \u043A\u0440\u0430\u0441\u043D\u043E\u044F\u0440\u0441\u043A: "KJA",
  \u0445\u0430\u0431\u0430\u0440\u043E\u0432\u0441\u043A: "KHV",
  \u043C\u0430\u0445\u0430\u0447\u043A\u0430\u043B\u0430: "MCX",
  \u0430\u043D\u0442\u0430\u043B\u044C\u044F: "AYT",
  \u0434\u0430\u043B\u0430\u043C\u0430\u043D: "DLM",
  \u0431\u043E\u0434\u0440\u0443\u043C: "BJV",
  \u0434\u0443\u0431\u0430\u0439: "DXB",
  "\u0448\u0430\u0440\u043C-\u044D\u043B\u044C-\u0448\u0435\u0439\u0445": "SSH",
  \u0445\u0443\u0440\u0433\u0430\u0434\u0430: "HRG",
  \u043D\u044F\u0447\u0430\u043D\u0433: "CXR",
  \u043A\u0430\u043C\u0440\u0430\u043D\u044C: "CXR",
  \u043F\u0445\u0443\u043A\u0435\u0442: "HKT",
  \u0435\u0440\u0435\u0432\u0430\u043D: "EVN",
  \u0442\u0431\u0438\u043B\u0438\u0441\u0438: "TBS",
  \u0431\u0430\u043A\u0443: "GYD",
  \u0442\u0430\u0448\u043A\u0435\u043D\u0442: "TAS",
  \u043C\u0438\u043D\u0441\u043A: "MSQ",
  \u0430\u043B\u043C\u0430\u0442\u044B: "ALA"
};
function airportCodeOf(label) {
  if (!label) return null;
  const coded = /\(([A-Z]{3})\)/.exec(label)?.[1] ?? /(?:^|[^A-Za-z])([A-Z]{3})(?![A-Za-z])/.exec(label)?.[1];
  if (coded) return coded;
  const first = label.toLowerCase().replace(/ё/g, "\u0435").split(/[,(]/)[0].trim().split(/\s+/)[0];
  return (first && CITY_AIRPORT[first]) ?? null;
}
function flightDateOf(reservation) {
  return reservation.startAt?.slice(0, 10) ?? reservation.startDate?.slice(0, 10) ?? null;
}
var FlightState = z2.enum([
  "scheduled",
  "checkin",
  "boarding",
  "departed",
  "arrived",
  "delayed",
  "cancelled",
  "diverted",
  "unknown"
]);
var LocalTime = z2.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/);
var Times = z2.object({
  scheduled: LocalTime.nullable().default(null),
  estimated: LocalTime.nullable().default(null),
  actual: LocalTime.nullable().default(null)
});
var FlightLink = z2.object({
  kind: z2.enum(["airport", "airline", "tracker", "checkin"]),
  label: z2.string(),
  url: z2.string().url()
});
var FlightStatus = z2.object({
  /** «SU 1024». */
  flight: z2.string(),
  date: z2.string().nullable(),
  from: z2.string().nullable(),
  to: z2.string().nullable(),
  state: FlightState,
  /** The airport's own words, when it has them: «Регистрация в 15:05». */
  statusText: z2.string().nullable().default(null),
  departure: Times,
  arrival: Times,
  /**
   * The expected departure as a real instant (UTC ISO), when the source gives
   * the airport's offset — what the monitor paces its checks by.
   */
  departsAt: z2.string().nullable().default(null),
  terminal: z2.string().nullable().default(null),
  /** «312,314-316» — as the airport writes them. */
  checkInDesks: z2.string().nullable().default(null),
  checkInStart: LocalTime.nullable().default(null),
  checkInEnd: LocalTime.nullable().default(null),
  gate: z2.string().nullable().default(null),
  /** The gate before the latest change, when the airport reports one. */
  previousGate: z2.string().nullable().default(null),
  boardingStart: LocalTime.nullable().default(null),
  boardingEnd: LocalTime.nullable().default(null),
  arrivalTerminal: z2.string().nullable().default(null),
  arrivalGate: z2.string().nullable().default(null),
  baggageBelt: z2.string().nullable().default(null),
  aircraft: z2.string().nullable().default(null),
  /** Who said so: «Шереметьево», «FlightAware». */
  source: z2.string().nullable().default(null),
  checkedAt: z2.string().nullable().default(null)
});
var FlightStatusEntry = z2.object({
  reservationId: z2.string().uuid(),
  flight: z2.string().nullable(),
  status: FlightStatus.nullable(),
  links: z2.array(FlightLink),
  /** Why there is no status: «появится за сутки до вылета». */
  note: z2.string().nullable().default(null)
});
var AIRPORT_BOARDS = {
  SVO: {
    name: "\u0428\u0435\u0440\u0435\u043C\u0435\u0442\u044C\u0435\u0432\u043E",
    url: (f) => `https://www.svo.aero/ru/timetable/departure${f ? `?search=${f.airline}${f.number}` : ""}`
  },
  DME: {
    name: "\u0414\u043E\u043C\u043E\u0434\u0435\u0434\u043E\u0432\u043E",
    url: (f) => `https://www.dme.ru/book/live-board/?direction=D${f ? `&searchText=${encodeURIComponent(`${f.airline} ${f.number}`)}` : ""}`
  },
  VKO: {
    name: "\u0412\u043D\u0443\u043A\u043E\u0432\u043E",
    url: () => "https://www.vnukovo.ru/ru/for-passengers/board/"
  },
  KZN: { name: "\u041A\u0430\u0437\u0430\u043D\u044C", url: () => "https://kazan.aero/on-line-schedule/" }
};
function airportName(code) {
  return (code && AIRPORT_BOARDS[code]?.name) ?? null;
}
var AIRLINE_STATUS = {
  S7: { name: "S7 Airlines", url: "https://www.s7.ru/ru/info/flight-status/" }
};
var AIRLINE_ICAO = {
  SU: "AFL",
  S7: "SBI",
  U6: "SVR",
  UT: "UTA",
  DP: "PBD",
  FV: "SDM",
  N4: "NWS",
  "5N": "AUL",
  WZ: "RWZ",
  ZF: "AZV",
  A4: "AZO",
  "6R": "DRU",
  YC: "LLM",
  "7R": "RLU",
  IO: "IAE",
  EO: "KAR",
  R3: "SYL",
  J2: "AHY",
  HY: "UZB",
  KC: "KZR",
  DV: "VSV",
  B2: "BRU",
  TK: "THY",
  PC: "PGT",
  XQ: "SXS",
  XC: "CAI",
  EK: "UAE",
  FZ: "FDB",
  QR: "QTR",
  EY: "ETD",
  G9: "ABY",
  MS: "MSR",
  SM: "MSC",
  UJ: "LMU",
  VN: "HVN",
  VJ: "VJC",
  CZ: "CSN",
  MU: "CES",
  CA: "CCA",
  HU: "CHH",
  "3U": "CSC",
  AI: "AIC",
  "6E": "IGO",
  TG: "THA",
  UL: "ALK",
  SQ: "SIA",
  W6: "WZZ",
  FR: "RYR",
  U2: "EZY",
  LH: "DLH",
  AF: "AFR",
  KL: "KLM",
  BA: "BAW",
  AY: "FIN",
  LO: "LOT",
  OS: "AUA",
  LX: "SWR",
  A3: "AEE",
  J9: "JZR",
  PS: "AUI",
  "9U": "MLD",
  KK: "KKK",
  VF: "TKJ",
  RO: "ROT",
  JU: "ASL",
  OU: "CTN",
  H9: "HIM",
  W9: "WUK",
  RJ: "RJA",
  GF: "GFA",
  WY: "OMA",
  SV: "SVA",
  ET: "ETH",
  KQ: "KQA",
  MH: "MAS",
  "5W": "AZG"
};
function flightAwareUrl(ident) {
  const icao = AIRLINE_ICAO[ident.airline];
  return `https://www.flightaware.com/live/flight/${icao ?? ident.airline}${ident.number}`;
}
var placeName = (label) => label?.replace(/\(.*?\)/g, "").split(",")[0]?.trim() || null;
function routeSearchUrl(route) {
  const from = placeName(route.fromLabel);
  const to = placeName(route.toLabel);
  if (!from || !to) return null;
  const query = new URLSearchParams({ fromName: from, toName: to });
  if (route.date) query.set("when", route.date);
  return `https://rasp.yandex.ru/search/plane/?${query}`;
}
function flightLinks(input) {
  const { ident, from } = input;
  const links = [];
  const board = from ? AIRPORT_BOARDS[from] : void 0;
  if (input.airportFlightUrl) {
    links.push({
      kind: "airport",
      label: `\u0420\u0435\u0439\u0441 \u043D\u0430 \u0441\u0430\u0439\u0442\u0435 \u0430\u044D\u0440\u043E\u043F\u043E\u0440\u0442\u0430${board ? ` ${board.name}` : ""}`,
      url: input.airportFlightUrl
    });
  } else if (board) {
    links.push({ kind: "airport", label: `\u0422\u0430\u0431\u043B\u043E \u0430\u044D\u0440\u043E\u043F\u043E\u0440\u0442\u0430 ${board.name}`, url: board.url(ident) });
  }
  const airline = ident ? AIRLINE_STATUS[ident.airline] : void 0;
  if (airline) links.push({ kind: "airline", label: `\u0421\u0442\u0430\u0442\u0443\u0441 \u0440\u0435\u0439\u0441\u0430 \u043D\u0430 \u0441\u0430\u0439\u0442\u0435 ${airline.name}`, url: airline.url });
  const timetable = input.route ? routeSearchUrl(input.route) : null;
  if (timetable) links.push({ kind: "tracker", label: "\u0420\u0435\u0439\u0441\u044B \u043D\u0430 \u042F\u043D\u0434\u0435\u043A\u0441 \u0420\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044F\u0445", url: timetable });
  if (ident) links.push({ kind: "tracker", label: "\u0420\u0435\u0439\u0441 \u043D\u0430 FlightAware", url: flightAwareUrl(ident) });
  if (input.checkInUrl) links.push({ kind: "checkin", label: "\u041E\u043D\u043B\u0430\u0439\u043D-\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F", url: input.checkInUrl });
  return links;
}
function minutesBetween(from, to) {
  if (!from || !to) return null;
  return Math.round((Date.parse(`${to}:00Z`) - Date.parse(`${from}:00Z`)) / 6e4);
}
function departureDelay(status) {
  const { scheduled, estimated, actual } = status.departure;
  const delay = minutesBetween(scheduled, actual ?? estimated);
  return delay !== null && delay >= 10 ? delay : null;
}
function flightStatusChanges(before, after) {
  const changes = [];
  const was = before;
  if (after.state === "cancelled" && was?.state !== "cancelled") changes.push("\u0420\u0435\u0439\u0441 \u043E\u0442\u043C\u0435\u043D\u0451\u043D");
  if (after.state === "diverted" && was?.state !== "diverted") changes.push("\u0420\u0435\u0439\u0441 \u0443\u0448\u0451\u043B \u043D\u0430 \u0437\u0430\u043F\u0430\u0441\u043D\u043E\u0439 \u0430\u044D\u0440\u043E\u0434\u0440\u043E\u043C");
  const delay = departureDelay(after);
  const wasDelay = was ? departureDelay(was) : null;
  if (delay !== null && after.state !== "cancelled" && (wasDelay === null || delay - wasDelay >= 15)) {
    const at = (after.departure.actual ?? after.departure.estimated)?.slice(11, 16);
    changes.push(`\u0412\u044B\u043B\u0435\u0442 \u0437\u0430\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u0442\u0441\u044F${at ? ` \u0434\u043E ${at}` : ""} (+${formatMinutes(delay)})`);
  }
  if (after.gate && was && after.gate !== was.gate) {
    changes.push(
      was.gate ? `\u0412\u044B\u0445\u043E\u0434 \u0441\u043C\u0435\u043D\u0438\u043B\u0441\u044F: ${was.gate} \u2192 ${after.gate}` : `\u0412\u044B\u0445\u043E\u0434 \u043D\u0430 \u043F\u043E\u0441\u0430\u0434\u043A\u0443: ${after.gate}`
    );
  }
  if (after.terminal && was?.terminal && after.terminal !== was.terminal) {
    changes.push(`\u0422\u0435\u0440\u043C\u0438\u043D\u0430\u043B \u0441\u043C\u0435\u043D\u0438\u043B\u0441\u044F: ${was.terminal} \u2192 ${after.terminal}`);
  }
  if (after.checkInDesks && was && !was.checkInDesks)
    changes.push(`\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F: \u0441\u0442\u043E\u0439\u043A\u0438 ${after.checkInDesks}`);
  return changes;
}
function formatMinutes(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} \u0447${m ? ` ${m} \u043C\u0438\u043D` : ""}` : `${m} \u043C\u0438\u043D`;
}
var FLIGHT_STATE_LABELS = {
  scheduled: "\u041F\u043E \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044E",
  checkin: "\u0418\u0434\u0451\u0442 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F",
  boarding: "\u041F\u043E\u0441\u0430\u0434\u043A\u0430",
  departed: "\u0412\u044B\u043B\u0435\u0442\u0435\u043B",
  arrived: "\u041F\u0440\u0438\u0431\u044B\u043B",
  delayed: "\u0417\u0430\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u0442\u0441\u044F",
  cancelled: "\u041E\u0442\u043C\u0435\u043D\u0451\u043D",
  diverted: "\u041D\u0430 \u0437\u0430\u043F\u0430\u0441\u043D\u043E\u043C \u0430\u044D\u0440\u043E\u0434\u0440\u043E\u043C\u0435",
  unknown: "\u0421\u0442\u0430\u0442\u0443\u0441 \u043D\u0435\u0438\u0437\u0432\u0435\u0441\u0442\u0435\u043D"
};

// src/trip-now.ts
var SOON_DAYS = 7;
var DAY_MS2 = 864e5;
function dayNumber(iso) {
  return Math.round(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / DAY_MS2);
}
function localIsoDate(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
function addIsoDays(iso, days) {
  return new Date((dayNumber(iso) + days) * DAY_MS2).toISOString().slice(0, 10);
}
function tripPhase(trip, today) {
  if (!trip.startDate || trip.completedAt) return null;
  const start = dayNumber(trip.startDate);
  const end = dayNumber(trip.endDate ?? trip.startDate);
  const now = dayNumber(today);
  if (now > end) return null;
  if (now >= start) return { kind: "ongoing", dayNumber: now - start + 1, dayCount: end - start + 1 };
  const daysUntil = start - now;
  return daysUntil <= SOON_DAYS ? { kind: "soon", daysUntil } : null;
}
function pickActiveTrip(trips, today) {
  let best = null;
  for (const trip of trips) {
    const phase = tripPhase(trip, today);
    if (!phase) continue;
    if (!best || rank(phase) < rank(best.phase)) best = { trip, phase };
  }
  return best;
}
function rank(phase) {
  return phase.kind === "ongoing" ? phase.dayNumber : 1e3 + phase.daysUntil;
}

// src/food.ts
var TOWN_LINK_M = 3e3;
var DISTRICT_LINK_M = 700;
var CITY_NAMES_WITHIN_M = 7e3;
var LANDMARK_NAMES_WITHIN_M = 600;
var SAME_SPOT_M = 300;
var STREET_WORD = /(^|[\s.])(ул|улица|пр|просп|проспект|пер|переулок|бул|бульвар|наб|набережная|пл|площадь|ш|шоссе|проезд|тупик|аллея|дорога|мкр|микрорайон|район|квартал|дом|корп|корпус|стр|строение|помещение|street|st|road|rd|avenue|ave|lane|square|caddesi|cad|cd|sokak|sok|sk|mahallesi|mah|bulvarı|odos|οδός)(?=$|[\s.])/i;
var NOT_A_TOWN = /^(у|в|во|на|по|за|возле|около|рядом|напротив|вход|выход|между|близ|центр|старый|старая|тихая|пешеходная|center|centre|near|old)(\s|$)/i;
var TOWN_PREFIX = /^(г|город|пос|посёлок|поселок|пгт|с|село|дер|деревня|д|ст|станица|town|city of)\.?\s+/i;
function addressTowns(address) {
  if (!address) return [];
  const out = [];
  const segments = address.split(/[,;]/).filter((raw) => !NOT_A_TOWN.test(raw.trim())).flatMap((raw) => raw.split("/"));
  for (const raw of segments) {
    const segment = raw.trim().replace(/\s+/g, " ");
    if (segment.length < 2 || segment.length > 32) continue;
    if (/\d/.test(segment)) continue;
    if (STREET_WORD.test(segment) || NOT_A_TOWN.test(segment)) continue;
    const name = segment.replace(TOWN_PREFIX, "").trim();
    if (!name || name.split(" ").length > 3) continue;
    out.push(name);
  }
  return out.reverse();
}
function townKey(name) {
  return name.toLowerCase().replace(TOWN_PREFIX, "").replace(/ё/g, "\u0435").trim();
}
function nameKey(name) {
  return name.toLowerCase().replace(/ё/g, "\u0435").replace(/[«»"'“”„]/g, "").replace(/\s+/g, " ").trim();
}
function hasCoords(p) {
  return p.lat !== null && p.lng !== null;
}
function collectSpots(places, days) {
  const daysOf2 = /* @__PURE__ */ new Map();
  for (const day of days) {
    for (const id of day.placeIds) {
      const set = daysOf2.get(id) ?? /* @__PURE__ */ new Set();
      set.add(day.dayIndex);
      daysOf2.set(id, set);
    }
  }
  const spots = [];
  for (const place of places) {
    if (place.kind !== "food") continue;
    const dayIndexes = [...daysOf2.get(place.id) ?? []];
    const twin = spots.find(
      (spot) => nameKey(spot.name) === nameKey(place.name) && (!hasCoords(spot) || !hasCoords(place) || haversineMeters(spot, place) <= SAME_SPOT_M)
    );
    if (twin) {
      twin.ids.push(place.id);
      twin.dayIndexes = [.../* @__PURE__ */ new Set([...twin.dayIndexes, ...dayIndexes])].sort((a, b) => a - b);
      if (!hasCoords(twin) && hasCoords(place)) {
        twin.id = place.id;
        twin.lat = place.lat;
        twin.lng = place.lng;
      }
      twin.address ??= place.address;
      continue;
    }
    spots.push({
      id: place.id,
      ids: [place.id],
      name: place.name,
      lat: place.lat,
      lng: place.lng,
      address: place.address,
      dayIndexes: dayIndexes.sort((a, b) => a - b)
    });
  }
  return spots;
}
function cluster(items, linkM) {
  const parent = items.map((_, i) => i);
  const find = (i) => parent[i] === i ? i : parent[i] = find(parent[i]);
  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      if (haversineMeters(items[i], items[j]) <= linkM) parent[find(i)] = find(j);
    }
  }
  const byRoot = /* @__PURE__ */ new Map();
  items.forEach((item, i) => {
    const root = find(i);
    byRoot.set(root, [...byRoot.get(root) ?? [], item]);
  });
  return [...byRoot.values()];
}
function nearestOf(point, candidates, withinM) {
  let best = null;
  for (const c of candidates) {
    if (!hasCoords(c)) continue;
    const d = haversineMeters(point, c);
    if (d <= withinM && (!best || d < best.d)) best = { item: c, d };
  }
  return best?.item ?? null;
}
function mostCommon(values) {
  const counts = /* @__PURE__ */ new Map();
  for (const value of values) {
    const key = townKey(value);
    const entry = counts.get(key) ?? { value, n: 0 };
    entry.n += 1;
    counts.set(key, entry);
  }
  let best = null;
  for (const entry of counts.values()) if (!best || entry.n > best.n) best = entry;
  return best?.value ?? null;
}
function firstDayOf(spots) {
  const all = spots.flatMap((s) => s.dayIndexes);
  return all.length ? Math.min(...all) : null;
}
function bySchedule(a, b) {
  const da = a.dayIndexes[0] ?? Number.POSITIVE_INFINITY;
  const db = b.dayIndexes[0] ?? Number.POSITIVE_INFINITY;
  return da - db || a.name.localeCompare(b.name, "ru");
}
function makeGroup(kind, label, spots) {
  const located = spots.filter(hasCoords);
  return {
    key: `${kind}:${townKey(label)}`,
    label,
    kind,
    spots: [...spots].sort(bySchedule),
    center: centroidOf(located),
    firstDay: firstDayOf(spots)
  };
}
var COMPASS = ["\u0421\u0435\u0432\u0435\u0440", "\u0421\u0435\u0432\u0435\u0440\u043E-\u0432\u043E\u0441\u0442\u043E\u043A", "\u0412\u043E\u0441\u0442\u043E\u043A", "\u042E\u0433\u043E-\u0432\u043E\u0441\u0442\u043E\u043A", "\u042E\u0433", "\u042E\u0433\u043E-\u0437\u0430\u043F\u0430\u0434", "\u0417\u0430\u043F\u0430\u0434", "\u0421\u0435\u0432\u0435\u0440\u043E-\u0437\u0430\u043F\u0430\u0434"];
function splitDistricts(town, landmarks) {
  const located = town.spots.filter(hasCoords);
  const parts = cluster(located, DISTRICT_LINK_M);
  if (parts.length < 2) return null;
  const centre = centroidOf(located);
  const used = /* @__PURE__ */ new Set();
  const groups = parts.map((part) => {
    const middle = centroidOf(part);
    const landmark = nearestOf(middle, landmarks, LANDMARK_NAMES_WITHIN_M);
    const street = mostCommon(
      part.flatMap(
        (s) => (s.address ?? "").split(",").map((x) => x.trim()).filter((x) => STREET_WORD.test(x) && !/\d/.test(x))
      )
    );
    let label;
    if (haversineMeters(middle, centre) < 500) label = "\u0426\u0435\u043D\u0442\u0440";
    else if (landmark) label = `\u0423 \xAB${landmark.name}\xBB`;
    else if (street) label = street;
    else {
      const bearing = Math.atan2(middle.lng - centre.lng, middle.lat - centre.lat) * 180 / Math.PI;
      label = COMPASS[Math.round((bearing + 360) % 360 / 45) % 8];
    }
    if (used.has(label)) label = `${label} \xB7 ${used.size + 1}`;
    used.add(label);
    return makeGroup("district", label, part);
  });
  const unlocated = town.spots.filter((s) => !hasCoords(s));
  if (unlocated.length) groups.push(makeGroup("unplaced", "\u0411\u0435\u0437 \u0442\u043E\u0447\u043D\u043E\u0433\u043E \u0430\u0434\u0440\u0435\u0441\u0430", unlocated));
  return groups;
}
function groupFoodPlaces(input) {
  const spots = collectSpots(input.places, input.days);
  const cities = input.places.filter((p) => p.kind === "city" && hasCoords(p));
  const landmarks = input.places.filter(
    (p) => !["food", "city", "transport", "airport", "service", "other"].includes(p.kind) && hasCoords(p)
  );
  const townCount = /* @__PURE__ */ new Map();
  for (const spot of spots) {
    for (const town of addressTowns(spot.address)) {
      townCount.set(townKey(town), (townCount.get(townKey(town)) ?? 0) + 1);
    }
  }
  const townOf = (spot) => {
    let best = null;
    for (const town of addressTowns(spot.address)) {
      const n = townCount.get(townKey(town)) ?? 0;
      if (!best || n > best.n) best = { name: town, n };
    }
    return best?.name ?? null;
  };
  const groups = /* @__PURE__ */ new Map();
  const add = (label, spot) => {
    const key = townKey(label);
    const entry = groups.get(key) ?? { label, spots: [] };
    entry.spots.push(spot);
    groups.set(key, entry);
  };
  const unplaced = [];
  const located = spots.filter(hasCoords);
  for (const part of cluster(located, TOWN_LINK_M)) {
    const middle = centroidOf(part);
    const city = nearestOf(middle, cities, CITY_NAMES_WITHIN_M);
    const written = mostCommon(part.map(townOf).filter((t) => Boolean(t)));
    const near = city ? null : nearestOf(middle, cities, 25e3);
    const landmark = nearestOf(middle, landmarks, 2e3);
    const label = city?.name ?? written ?? (near ? `${near.name} \xB7 \u043E\u043A\u0440\u0435\u0441\u0442\u043D\u043E\u0441\u0442\u0438` : landmark ? `\u0423 \xAB${landmark.name}\xBB` : null);
    for (const spot of part) {
      if (label) add(label, spot);
      else unplaced.push(spot);
    }
  }
  for (const spot of spots) {
    if (hasCoords(spot)) continue;
    const town = townOf(spot);
    if (town) add(town, spot);
    else unplaced.push(spot);
  }
  let out = [...groups.values()].map((g) => makeGroup("town", g.label, g.spots));
  let scope = "towns";
  if (out.length === 1 && out[0].spots.length >= 6) {
    const districts = splitDistricts(out[0], landmarks);
    if (districts) {
      out = districts;
      scope = "districts";
    }
  }
  out.sort(
    (a, b) => (a.firstDay ?? Number.POSITIVE_INFINITY) - (b.firstDay ?? Number.POSITIVE_INFINITY) || a.label.localeCompare(b.label, "ru")
  );
  const leftover = out.find((g) => g.kind === "unplaced");
  if (leftover) out = out.filter((g) => g !== leftover);
  const rest = [...leftover?.spots ?? [], ...unplaced];
  if (rest.length) out.push(makeGroup("unplaced", "\u0411\u0435\u0437 \u0442\u043E\u0447\u043D\u043E\u0433\u043E \u0430\u0434\u0440\u0435\u0441\u0430", rest));
  return { scope, groups: out };
}
function rankFoodGroups(groups, here) {
  const ranked = groups.map((group) => {
    const spots = group.spots.map((spot) => ({
      ...spot,
      distanceM: here && hasCoords(spot) ? haversineMeters(here, spot) : null
    }));
    if (here) {
      spots.sort((a, b) => (a.distanceM ?? Number.POSITIVE_INFINITY) - (b.distanceM ?? Number.POSITIVE_INFINITY));
    }
    const distances = spots.flatMap((s) => s.distanceM === null ? [] : [s.distanceM]);
    return { ...group, spots, nearestM: distances.length ? Math.min(...distances) : null };
  });
  if (!here) return ranked;
  return ranked.sort((a, b) => {
    if (a.kind === "unplaced" || b.kind === "unplaced") return a.kind === "unplaced" ? 1 : -1;
    return (a.nearestM ?? Number.POSITIVE_INFINITY) - (b.nearestM ?? Number.POSITIVE_INFINITY);
  });
}
function formatDistance(meters) {
  if (meters < 50) return "\u0440\u044F\u0434\u043E\u043C";
  if (meters < 1e3) return `${Math.round(meters / 10) * 10} \u043C`;
  if (meters < 1e4) return `${(meters / 1e3).toFixed(1).replace(".", ",")} \u043A\u043C`;
  return `${Math.round(meters / 1e3)} \u043A\u043C`;
}
function walkMinutes(meters) {
  const minutes = Math.max(1, Math.round(meters * 1.3 / 80));
  return minutes <= 30 ? minutes : null;
}

// src/maps.ts
var MAP_ENGINES = ["yandex", "google"];
var WALK_MAX_M = 2e3;
var ROAD_MAX_M = 8e5;
function legMode(a, b) {
  const d = haversineMeters(a, b);
  if (d <= WALK_MAX_M) return "foot";
  if (d <= ROAD_MAX_M) return "car";
  return "straight";
}
function legKey(mode, a, b) {
  const p = (x) => x.toFixed(5);
  return `${mode}:${p(a.lat)},${p(a.lng)};${p(b.lat)},${p(b.lng)}`;
}
function legsOf(points) {
  const out = [];
  for (let i = 1; i < points.length; i += 1) {
    const from = points[i - 1];
    const to = points[i];
    if (haversineMeters(from, to) < 15) continue;
    out.push({ from, to, mode: legMode(from, to) });
  }
  return out;
}
function simplifyPath(path, tolerance = 5e-5) {
  if (path.length <= 2) return path;
  const keep = new Uint8Array(path.length);
  keep[0] = 1;
  keep[path.length - 1] = 1;
  const stack = [[0, path.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop();
    const [ay, ax] = path[start];
    const [by, bx] = path[end];
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let worst = -1;
    let worstD = 0;
    for (let i = start + 1; i < end; i += 1) {
      const [py, px] = path[i];
      let d;
      if (len2 === 0) {
        d = Math.hypot(px - ax, py - ay);
      } else {
        const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
        d = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
      }
      if (d > worstD) {
        worstD = d;
        worst = i;
      }
    }
    if (worst > 0 && worstD > tolerance) {
      keep[worst] = 1;
      stack.push([start, worst], [worst, end]);
    }
  }
  return path.filter((_, i) => keep[i] === 1);
}
var GOOGLE_MAX_WAYPOINTS = 9;
var YANDEX_MAX_POINTS = 20;
var coord2 = (p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
function appRouteUrl(engine, points, walking) {
  if (points.length < 2) return null;
  if (engine === "yandex") {
    return `https://yandex.ru/maps/?rtext=${points.map(coord2).join("~")}&rtt=${walking ? "pd" : "auto"}`;
  }
  const origin = points[0];
  const destination = points[points.length - 1];
  const middle = points.slice(1, -1);
  const waypoints = middle.length ? `&waypoints=${middle.map(coord2).join("%7C")}` : "";
  return `https://www.google.com/maps/dir/?api=1&origin=${coord2(origin)}&destination=${coord2(destination)}${waypoints}&travelmode=${walking ? "walking" : "driving"}`;
}
function chunkRoute(points, engine) {
  const max = engine === "google" ? GOOGLE_MAX_WAYPOINTS + 2 : YANDEX_MAX_POINTS;
  if (points.length <= max) return [points];
  const out = [];
  for (let start = 0; start < points.length - 1; start += max - 1) {
    out.push(points.slice(start, start + max));
  }
  return out;
}
function yandexPinsUrl(points) {
  if (!points.length) return null;
  const shown = points.slice(0, 100);
  const pins = shown.map((p) => `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`);
  const lats = shown.map((p) => p.lat);
  const lngs = shown.map((p) => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const center = `${((minLng + maxLng) / 2).toFixed(6)},${((minLat + maxLat) / 2).toFixed(6)}`;
  return `https://yandex.ru/maps/?ll=${center}&z=${zoomToFit(maxLat - minLat, maxLng - minLng)}&l=map&pt=${pins.join("~")}`;
}
function zoomToFit(latSpan, lngSpan) {
  const span = Math.max(lngSpan, latSpan * 1.5);
  if (span <= 0) return 15;
  const zoom = Math.floor(Math.log2(360 * 360 / 256 / (span * 1.3)));
  return Math.max(3, Math.min(16, zoom));
}
function pointFromMapUrl(raw) {
  let url;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, "");
  let text = url.href;
  try {
    text = decodeURIComponent(url.href);
  } catch {
  }
  const valid = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && (lat !== 0 || lng !== 0);
  if (/(^|\.)google\.[a-z.]+$/.test(host) || host === "maps.google.com") {
    const name = /\/maps\/place\/([^/@]+)/.exec(url.pathname)?.[1];
    const placeName2 = name ? decodeURIComponent(name.replace(/\+/g, " ")) : null;
    const data = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/.exec(text);
    if (data) {
      const lat = Number(data[1]);
      const lng = Number(data[2]);
      if (valid(lat, lng)) return { lat, lng, name: placeName2 };
    }
    for (const key of ["q", "query", "destination", "ll"]) {
      const value = url.searchParams.get(key);
      const m = value ? /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/.exec(value) : null;
      if (m && valid(Number(m[1]), Number(m[2]))) return { lat: Number(m[1]), lng: Number(m[2]), name: placeName2 };
    }
    const at = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(url.pathname);
    if (at && valid(Number(at[1]), Number(at[2]))) return { lat: Number(at[1]), lng: Number(at[2]), name: placeName2 };
    return null;
  }
  if (/(^|\.)yandex\.[a-z.]+$/.test(host) || /(^|\.)ya\.ru$/.test(host)) {
    const textParam = url.searchParams.get("text");
    const orgName = /\/org\/([^/]+)\/\d+/.exec(url.pathname)?.[1];
    const name = textParam ?? (orgName ? decodeURIComponent(orgName).replace(/_/g, " ") : null);
    for (const key of ["whatshere[point]", "pt", "ll"]) {
      const value = url.searchParams.get(key);
      const m = value ? /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.exec(value) : null;
      if (m) {
        const lng = Number(m[1]);
        const lat = Number(m[2]);
        if (valid(lat, lng)) return { lat, lng, name };
      }
    }
    return null;
  }
  return null;
}
function isMapShortLink(raw) {
  try {
    const host = new URL(raw.trim()).hostname.replace(/^www\./, "");
    return host === "maps.app.goo.gl" || host === "goo.gl" || (/(^|\.)yandex\.[a-z.]+$/.test(host) || host === "ya.ru") && /\/maps\/-\//.test(new URL(raw.trim()).pathname);
  } catch {
    return false;
  }
}
function dayForPoint(point, days, note, maxM = 3e4) {
  const named = note ? /(?:^|[^\p{L}])(?:день|day|д)\s*(\d{1,2})(?![\d])/iu.exec(note) : null;
  if (named) {
    const index = Number(named[1]);
    if (days.some((day) => day.dayIndex === index)) return index;
  }
  let best = null;
  for (const day of days) {
    for (const stop of day.points) {
      const d = haversineMeters(point, stop);
      if (d <= maxM && (!best || d < best.d)) best = { dayIndex: day.dayIndex, d };
    }
  }
  return best?.dayIndex ?? null;
}
var KML_ICONS = {
  lodging: "lodging",
  food: "dining",
  beach: "swimming",
  nature: "parks",
  viewpoint: "camera",
  sight: "museum",
  ruins: "museum",
  city: "flag",
  airport: "airports",
  transport: "cabs",
  shopping: "shopping",
  activity: "hiker",
  service: "info-i"
};
function buildKml(input) {
  const kinds = /* @__PURE__ */ new Set();
  for (const day of input.days) for (const p of day.placemarks) kinds.add(p.kind);
  for (const p of input.other) kinds.add(p.kind);
  const styles = [...kinds].map(
    (kind) => `    <Style id="k-${escapeXml(kind)}"><IconStyle><Icon><href>https://maps.google.com/mapfiles/kml/shapes/${KML_ICONS[kind] ?? "placemark_circle"}.png</href></Icon></IconStyle></Style>`
  ).join("\n");
  const placemark = (p) => [
    "      <Placemark>",
    `        <name>${escapeXml(p.name)}</name>`,
    p.description ? `        <description>${escapeXml(p.description)}</description>` : null,
    `        <styleUrl>#k-${escapeXml(p.kind)}</styleUrl>`,
    `        <Point><coordinates>${p.lng.toFixed(6)},${p.lat.toFixed(6)}</coordinates></Point>`,
    "      </Placemark>"
  ].filter(Boolean).join("\n");
  const folder = (name, placemarks, line) => [
    "    <Folder>",
    `      <name>${escapeXml(name)}</name>`,
    ...placemarks.map(placemark),
    line.length > 1 ? [
      "      <Placemark>",
      `        <name>${escapeXml(`${name} \u2014 \u043C\u0430\u0440\u0448\u0440\u0443\u0442`)}</name>`,
      "        <styleUrl>#route</styleUrl>",
      `        <LineString><tessellate>1</tessellate><coordinates>${line.map(([lat, lng]) => `${lng.toFixed(5)},${lat.toFixed(5)}`).join(" ")}</coordinates></LineString>`,
      "      </Placemark>"
    ].join("\n") : null,
    "    </Folder>"
  ].filter(Boolean).join("\n");
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    "  <Document>",
    `    <name>${escapeXml(input.name)}</name>`,
    input.description ? `    <description>${escapeXml(input.description)}</description>` : null,
    '    <Style id="route"><LineStyle><color>ff4a65e2</color><width>4</width></LineStyle></Style>',
    styles || null,
    ...input.days.filter((d) => d.placemarks.length).map((d) => folder(d.name, d.placemarks, d.line)),
    input.other.length ? folder("\u0411\u0435\u0437 \u0434\u043D\u044F", input.other, []) : null,
    "  </Document>",
    "</kml>",
    ""
  ].filter((line) => line !== null).join("\n");
}
var KIND_WORDS = [
  ["lodging", /отель|гостиниц|хостел|апартамент|гостев|вилл[аы]|коттедж|\bдом\b|hotel|hostel|apart|guest ?house|villa|resort|\binn\b|b&b|pension|pansiyon|otel/iu],
  ["airport", /аэропорт|airport|havaliman|aeroporto|aéroport|flughafen/iu],
  ["food", /ресторан|кафе|бар\b|пиццер|кофейн|пекарн|столов|бистро|restaurant|caf[eé]|coffee|bistro|pizz|bakery|\bbar\b|pub\b|trattoria|osteria|lokanta|meyhane|kebap|börek|sofras/iu],
  ["beach", /пляж|beach|plaj|playa|spiaggia|plage/iu],
  ["viewpoint", /смотров|обзорн|viewpoint|lookout|panoram|seyir|mirador|belvedere/iu],
  ["ruins", /руин|развалин|античн|ruins|ancient|antik|harabe/iu],
  ["nature", /парк|сад\b|озер|водопад|гора\b|каньон|заповедн|park|garden|lake|waterfall|canyon|mount|national|şelale|göl/iu],
  ["shopping", /рынок|базар|молл|магазин|торгов|market|bazaar|mall|shop|çarşı|outlet/iu],
  ["transport", /вокзал|станци|автовокзал|порт\b|причал|прокат|station|terminal|port\b|pier|ferry|otogar|car rental|rent a car/iu],
  ["sight", /музей|собор|церк|храм|мечет|замок|крепост|дворец|монастыр|museum|cathedral|church|mosque|camii|castle|fortress|palace|monastery|temple/iu]
];
function guessPlaceKind(name) {
  for (const [kind, words] of KIND_WORDS) if (words.test(name)) return kind;
  return "sight";
}

// src/caveats.ts
var WEEKDAY_SHORT = ["", "\u043F\u043D", "\u0432\u0442", "\u0441\u0440", "\u0447\u0442", "\u043F\u0442", "\u0441\u0431", "\u0432\u0441"];
var WEEKDAY_IN = ["", "\u0432 \u043F\u043E\u043D\u0435\u0434\u0435\u043B\u044C\u043D\u0438\u043A", "\u0432\u043E \u0432\u0442\u043E\u0440\u043D\u0438\u043A", "\u0432 \u0441\u0440\u0435\u0434\u0443", "\u0432 \u0447\u0435\u0442\u0432\u0435\u0440\u0433", "\u0432 \u043F\u044F\u0442\u043D\u0438\u0446\u0443", "\u0432 \u0441\u0443\u0431\u0431\u043E\u0442\u0443", "\u0432 \u0432\u043E\u0441\u043A\u0440\u0435\u0441\u0435\u043D\u044C\u0435"];
function isoWeekday(date) {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!parsed) return null;
  const day = new Date(Date.UTC(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3]))).getUTCDay();
  return day === 0 ? 7 : day;
}
var minutesOf = (time) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};
var EARLY_MIN = 60;
var LATE_MIN = 5;
function caveatConflicts(item, caveats) {
  const out = [];
  const weekday = item.date ? isoWeekday(item.date) : null;
  const start = item.startTime ? minutesOf(item.startTime) : null;
  for (const caveat of caveats) {
    if (weekday && caveat.closedDays.includes(weekday)) {
      out.push({ caveat, message: `${capitalise(WEEKDAY_IN[weekday] ?? "")} \u0441\u044E\u0434\u0430 \u043D\u0435 \u043F\u043E\u043F\u0430\u0441\u0442\u044C` });
      continue;
    }
    if (start === null || !caveat.times.length) continue;
    const fits = caveat.times.some((time) => {
      const at = minutesOf(time);
      return at !== null && start >= at - EARLY_MIN && start <= at + LATE_MIN;
    });
    if (!fits) {
      out.push({
        caveat,
        message: `\u041F\u043E \u043F\u043B\u0430\u043D\u0443 ${item.startTime}, \u0430 \u043F\u043E\u043F\u0430\u0441\u0442\u044C \u043C\u043E\u0436\u043D\u043E \u0442\u043E\u043B\u044C\u043A\u043E ${joinTimes(caveat.times)}`
      });
    }
  }
  return out;
}
function joinTimes(times2) {
  const sorted = [...times2].sort();
  if (sorted.length <= 1) return `\u0432 ${sorted[0] ?? ""}`;
  return `\u0432 ${sorted.slice(0, -1).join(", ")} \u0438\u043B\u0438 ${sorted[sorted.length - 1]}`;
}
function weekdaysLabel(days) {
  return [...days].sort((a, b) => a - b).map((d) => WEEKDAY_SHORT[d] ?? "").filter(Boolean).join(", ");
}
function sortCaveats(caveats) {
  return [...caveats].sort((a, b) => Number(b.important) - Number(a.important));
}
function capitalise(text) {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

// src/day-facts.ts
function minutesOf2(time) {
  const match = /^(\d{1,2}):(\d{2})$/.exec((time ?? "").trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h <= 24 && m < 60 ? h * 60 + m : null;
}
function timeOf(minutes) {
  const m = (Math.round(minutes) % 1440 + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
var DAY_WORDS = {
  \u043F\u043D: 1,
  \u0432\u0442: 2,
  \u0441\u0440: 3,
  \u0447\u0442: 4,
  \u043F\u0442: 5,
  \u0441\u0431: 6,
  \u0432\u0441: 7,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 7
};
function hoursOn(line, weekday) {
  const text = (line ?? "").toLowerCase().replace(/[—−]/g, "\u2013").trim();
  if (!text) return null;
  if (/^(закрыто|closed)/.test(text)) return "closed";
  let named = false;
  let fallback = null;
  for (const raw of text.split(";")) {
    const part = raw.trim();
    if (!part) continue;
    const head = /^((?:(?:пн|вт|ср|чт|пт|сб|вс|mon|tue|wed|thu|fri|sat|sun)[\s,–-]*)+)/.exec(part);
    const spans = spansOf(part);
    const closed = /выходн|закрыт|closed/.test(part);
    if (!head) {
      fallback = closed && !spans.length ? "closed" : spans.length ? spans : fallback;
      continue;
    }
    named = true;
    if (!daysOf(head[1]).has(weekday)) continue;
    if (closed && !spans.length) return "closed";
    if (spans.length) return spans;
  }
  if (fallback) return fallback;
  return named ? "closed" : null;
}
function daysOf(list) {
  const days = /* @__PURE__ */ new Set();
  for (const piece of list.split(",")) {
    const range = piece.trim().split(/\s*[–-]\s*/).map((w) => DAY_WORDS[w.trim().slice(0, 3)] ?? DAY_WORDS[w.trim().slice(0, 2)]);
    const from = range[0];
    const to = range[1] ?? from;
    if (!from || !to) continue;
    for (let d = from; ; d = d % 7 + 1) {
      days.add(d);
      if (d === to || days.size >= 7) break;
    }
  }
  return days;
}
function spansOf(part) {
  if (/круглосуточно|24 ?hours|open 24/.test(part)) return [{ open: 0, close: 1440 }];
  const [main, pause] = part.split(/перерыв|break/);
  const ranges = [...(main ?? "").matchAll(/(\d{1,2}:\d{2})\s*–\s*(\d{1,2}:\d{2})/g)].flatMap((m) => {
    const open = minutesOf2(m[1]);
    let close = minutesOf2(m[2]);
    if (open === null || close === null) return [];
    if (close <= open) close += 1440;
    return [{ open, close }];
  });
  const gap = /(\d{1,2}:\d{2})\s*–\s*(\d{1,2}:\d{2})/.exec(pause ?? "");
  const gapFrom = minutesOf2(gap?.[1]);
  const gapTo = minutesOf2(gap?.[2]);
  if (gapFrom === null || gapTo === null) return ranges;
  return ranges.flatMap(
    (r) => gapFrom > r.open && gapTo < r.close ? [{ open: r.open, close: gapFrom }, { open: gapTo, close: r.close }] : [r]
  );
}
function visitFits(hours, start, minutes) {
  if (hours === null) return null;
  if (hours === "closed") return "closed";
  const end = start + Math.max(0, minutes);
  if (hours.some((s) => start >= s.open && end <= s.close + 5)) return "ok";
  const first = Math.min(...hours.map((s) => s.open));
  const last = Math.max(...hours.map((s) => s.close));
  if (start < first) return "before";
  if (end > last + 5) return "after";
  return "break";
}
function sunTimes(date, point, utcOffsetMinutes) {
  const day = Date.parse(`${date.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(day)) return null;
  const yearStart = Date.UTC(new Date(day).getUTCFullYear(), 0, 1);
  const n = Math.floor((day - yearStart) / 864e5) + 1;
  const g = 2 * Math.PI / 365 * (n - 1);
  const eq = 229.18 * (75e-6 + 1868e-6 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const decl = 6918e-6 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 6758e-6 * Math.cos(2 * g) + 907e-6 * Math.sin(2 * g) - 2697e-6 * Math.cos(3 * g) + 148e-5 * Math.sin(3 * g);
  const lat = point.lat * Math.PI / 180;
  const cosH = Math.cos(90.833 * Math.PI / 180) / (Math.cos(lat) * Math.cos(decl)) - Math.tan(lat) * Math.tan(decl);
  if (cosH < -1 || cosH > 1) return null;
  const ha = Math.acos(cosH) * 180 / Math.PI;
  const noon = 720 - 4 * point.lng - eq + utcOffsetMinutes;
  return { sunrise: Math.round(noon - 4 * ha), sunset: Math.round(noon + 4 * ha) };
}
function zoneOffsetMinutes(timeZone, at) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).formatToParts(at);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    const local = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"));
    return Math.round((local - Math.floor(at.getTime() / 6e4) * 6e4) / 6e4);
  } catch {
    return null;
  }
}
function localClock(at, offsetMinutes) {
  const shifted = new Date(at.getTime() + offsetMinutes * 6e4);
  return {
    date: shifted.toISOString().slice(0, 10),
    minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes()
  };
}
function guessOffsetMinutes(countries, lng, at) {
  const zone = countries.includes("RU") ? lng < 23 ? "Europe/Kaliningrad" : lng < 50 ? "Europe/Moscow" : lng < 55 ? "Europe/Samara" : lng < 66 ? "Asia/Yekaterinburg" : lng < 75 ? "Asia/Omsk" : lng < 90 ? "Asia/Novosibirsk" : lng < 106 ? "Asia/Krasnoyarsk" : lng < 120 ? "Asia/Irkutsk" : lng < 135 ? "Asia/Yakutsk" : lng < 150 ? "Asia/Vladivostok" : "Asia/Kamchatka" : null;
  return (zone ? zoneOffsetMinutes(zone, at) : null) ?? Math.round(lng / 15) * 60;
}
function footMinutes(meters) {
  return Math.max(1, Math.round(meters * 1.3 / 4800 * 60));
}
function driveMinutes(meters) {
  const road = meters * 1.35;
  const speed = road < 1e4 ? 25e3 : road < 4e4 ? 45e3 : 7e4;
  return Math.max(5, Math.round(road / speed * 60) + 5);
}
var FIXED_ACTIVITY_KINDS = ["flight", "checkin", "checkout", "transfer"];
function shiftDay(items, fromId, startTime) {
  const timed = items.map((item, index) => ({ item, index, start: minutesOf2(item.startTime) })).filter((x) => x.start !== null).sort((a, b) => a.start - b.start || a.index - b.index);
  const at = timed.findIndex((x) => x.item.id === fromId);
  const begin = minutesOf2(startTime);
  const out = { changes: [], conflicts: [], overflow: [] };
  if (at < 0 || begin === null) return out;
  const dur = (item) => item.durationMinutes ?? 30;
  const first = timed[at];
  if (begin !== first.start) out.changes.push({ id: first.item.id, title: first.item.title, from: timeOf(first.start), to: timeOf(begin) });
  let prevEnd = begin + dur(first.item);
  let prevOrigEnd = first.start + dur(first.item);
  for (const { item, start } of timed.slice(at + 1)) {
    const gap = Math.min(30, Math.max(0, start - prevOrigEnd));
    prevOrigEnd = Math.max(prevOrigEnd, start + dur(item));
    const wanted = prevEnd + gap;
    if (item.fixed || item.done) {
      if (item.fixed && wanted > start + 2) out.conflicts.push({ title: item.title, minutes: wanted - start });
      prevEnd = Math.max(prevEnd, start + dur(item));
      continue;
    }
    let next = start;
    if (wanted > start) next = Math.ceil(wanted / 5) * 5;
    if (next + dur(item) > 24 * 60) {
      out.overflow.push(item.title);
      continue;
    }
    if (next !== start) out.changes.push({ id: item.id, title: item.title, from: timeOf(start), to: timeOf(next) });
    prevEnd = next + dur(item);
  }
  return out;
}
function currentShiftItem(items, nowMinutes) {
  const open = items.filter((i) => !i.done && !i.fixed && minutesOf2(i.startTime) !== null).sort((a, b) => minutesOf2(a.startTime) - minutesOf2(b.startTime));
  const current = open.find((i) => minutesOf2(i.startTime) + (i.durationMinutes ?? 30) > nowMinutes);
  return (current ?? open[open.length - 1])?.id ?? null;
}
function placeHoursOn(hours, weekday) {
  if (!hours) return null;
  if (hours.closedDays?.includes(weekday)) return "closed";
  return hoursOn(hours.line, weekday);
}
function hoursConflict(item, hours) {
  if (!hours || !item.date) return null;
  const weekday = isoWeekdayOf(item.date);
  const start = minutesOf2(item.startTime);
  if (!weekday) return null;
  const spans = placeHoursOn(hours, weekday);
  if (spans === "closed") return `${WEEKDAY_ON[weekday]} \u0437\u0430\u043A\u0440\u044B\u0442\u043E`;
  if (start === null || !spans) return null;
  const fit = visitFits(spans, start, item.durationMinutes ?? 30);
  if (!fit || fit === "ok") return null;
  const first = Math.min(...spans.map((s) => s.open));
  const last = Math.max(...spans.map((s) => s.close));
  if (fit === "before") return `\u041E\u0442\u043A\u0440\u043E\u0435\u0442\u0441\u044F \u0442\u043E\u043B\u044C\u043A\u043E \u0432 ${timeOf(first)}`;
  if (fit === "after") return `\u0417\u0430\u043A\u0440\u044B\u0432\u0430\u0435\u0442\u0441\u044F \u0432 ${timeOf(last)} \u2014 \u0432\u0438\u0437\u0438\u0442 \u043D\u0435 \u0443\u0441\u043F\u0435\u0435\u0442 \u0437\u0430\u043A\u043E\u043D\u0447\u0438\u0442\u044C\u0441\u044F`;
  return "\u0412 \u044D\u0442\u043E \u0432\u0440\u0435\u043C\u044F \u043F\u0435\u0440\u0435\u0440\u044B\u0432";
}
var WEEKDAY_ON = ["", "\u0412 \u043F\u043E\u043D\u0435\u0434\u0435\u043B\u044C\u043D\u0438\u043A", "\u0412\u043E \u0432\u0442\u043E\u0440\u043D\u0438\u043A", "\u0412 \u0441\u0440\u0435\u0434\u0443", "\u0412 \u0447\u0435\u0442\u0432\u0435\u0440\u0433", "\u0412 \u043F\u044F\u0442\u043D\u0438\u0446\u0443", "\u0412 \u0441\u0443\u0431\u0431\u043E\u0442\u0443", "\u0412 \u0432\u043E\u0441\u043A\u0440\u0435\u0441\u0435\u043D\u044C\u0435"];
function isoWeekdayOf(date) {
  const t = Date.parse(`${date.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(t)) return null;
  return (new Date(t).getUTCDay() + 6) % 7 + 1;
}

// src/schemas/common.ts
import { z as z3 } from "zod";
var IsoDate = z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
var ClockTime = z3.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM");
var IsoDateTime = z3.string().datetime({ offset: true });
var Uuid = z3.string().uuid();
var Latitude = z3.number().min(-90).max(90);
var Longitude = z3.number().min(-180).max(180);
var LatLng = z3.tuple([Latitude, Longitude]);
var GeoPoint = z3.object({
  lat: Latitude,
  lng: Longitude
});
var Money = z3.object({
  amount: z3.number(),
  currency: z3.string().length(3).toUpperCase()
});
var ExternalLink = z3.object({
  label: z3.string().min(1).max(120),
  url: z3.string().url(),
  kind: z3.enum(["maps", "booking", "official", "article", "video", "other"]).default("other")
});
var SyncMeta = z3.object({
  rev: z3.number().int().nonnegative(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  deletedAt: IsoDateTime.nullable().default(null)
});
var PaginationQuery = z3.object({
  cursor: z3.string().optional(),
  limit: z3.coerce.number().int().min(1).max(100).default(20)
});
function paginated(item) {
  return z3.object({
    items: z3.array(item),
    nextCursor: z3.string().nullable(),
    total: z3.number().int().nonnegative().optional()
  });
}
var ApiError = z3.object({
  error: z3.object({
    code: z3.string(),
    message: z3.string(),
    details: z3.unknown().optional()
  })
});
var OkResponse = z3.object({ ok: z3.literal(true) });

// src/schemas/privacy.ts
import { z as z4 } from "zod";
var PrivacyProfile = z4.object({
  /** Real calendar dates. Off → the public view shows "День 3", month and season only. */
  showExactDates: z4.boolean().default(false),
  /** Names/handles of the travellers. */
  showTravelers: z4.boolean().default(false),
  /** Where you slept — hotel names and their pins. */
  showLodging: z4.boolean().default(true),
  /** Address, phone, confirmation number, door PIN. Never exposed publicly. */
  showLodgingDetails: z4.literal(false).default(false),
  /** Transport legs: "Москва → Даламан, Pegasus". Flight numbers stay hidden regardless. */
  showTransport: z4.boolean().default(true),
  /** Prices you paid. */
  showPrices: z4.boolean().default(false),
  /** Uploaded PDFs/tickets. Structurally forced off — documents are owner-only. */
  showDocuments: z4.literal(false).default(false),
  /** Photo gallery (each photo additionally has its own visibility). */
  showPhotos: z4.boolean().default(true),
  /** Free-form personal notes attached to days and activities. */
  showPersonalNotes: z4.boolean().default(false),
  /** Restaurant picks. */
  showFood: z4.boolean().default(true),
  /** Map with the route line. */
  showMap: z4.boolean().default(true),
  /** Let signed-in visitors copy this trip as a starting point for their own. */
  allowForking: z4.boolean().default(true)
});
var defaultPrivacyProfile = () => PrivacyProfile.parse({});
var openPrivacyProfile = () => PrivacyProfile.parse({
  showExactDates: true,
  showTravelers: true,
  showLodging: true,
  showTransport: true,
  showPrices: true,
  showPhotos: true,
  showPersonalNotes: true,
  showFood: true,
  showMap: true,
  allowForking: true
});
var ViewerRole = z4.enum(["owner", "editor", "viewer", "guest"]);
var ViewerContext = z4.object({
  userId: z4.string().nullable(),
  role: ViewerRole,
  isAdmin: z4.boolean().default(false)
});
function seesPrivateData(viewer) {
  return viewer.role === "owner" || viewer.role === "editor" || viewer.isAdmin;
}
var guestViewer = () => ({ userId: null, role: "guest", isAdmin: false });

// src/schemas/user.ts
import { z as z5 } from "zod";
var HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9_-]{1,30})[a-z0-9]$/;
var Handle = z5.string().trim().toLowerCase().min(3, "\u043C\u0438\u043D\u0438\u043C\u0443\u043C 3 \u0441\u0438\u043C\u0432\u043E\u043B\u0430").max(32, "\u043C\u0430\u043A\u0441\u0438\u043C\u0443\u043C 32 \u0441\u0438\u043C\u0432\u043E\u043B\u0430").regex(HANDLE_PATTERN, "\u0442\u043E\u043B\u044C\u043A\u043E \u043B\u0430\u0442\u0438\u043D\u0438\u0446\u0430, \u0446\u0438\u0444\u0440\u044B, \u0434\u0435\u0444\u0438\u0441 \u0438 \u043F\u043E\u0434\u0447\u0451\u0440\u043A\u0438\u0432\u0430\u043D\u0438\u0435");
var Password = z5.string().min(10, "\u043C\u0438\u043D\u0438\u043C\u0443\u043C 10 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432").max(200, "\u043C\u0430\u043A\u0441\u0438\u043C\u0443\u043C 200 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432");
var UserPreferences = z5.object({
  locale: z5.enum(["ru", "en"]).default("ru"),
  theme: z5.enum(["system", "light", "dark"]).default("system"),
  units: z5.enum(["metric", "imperial"]).default("metric"),
  defaultCurrency: z5.string().length(3).default("EUR"),
  /** Privacy defaults applied to every newly created trip. */
  defaultTripVisibility: z5.enum(["private", "link", "public"]).default("private"),
  /** Opt-in: let your public trips be listed in the gallery on the landing page. */
  listInGallery: z5.boolean().default(false),
  /**
   * Citizenships (ISO-3166-1 alpha-2), set once in the profile and reused as
   * the default for every new trip — the visa lookup needs a citizenship, and
   * typing it again for every trip is exactly the kind of repetition an
   * account-level default exists to remove. A trip can still override it.
   */
  citizenships: z5.array(z5.string().length(2).toUpperCase()).max(5).default([]),
  /**
   * The country the traveller lives in (ISO-3166-1 alpha-2): what «abroad»
   * means to them — the deal search's «Не РФ» reads «Не Чехия» for someone in
   * Czechia. Not personal data: a country, chosen, and changeable any time.
   */
  homeCountry: z5.string().length(2).toUpperCase().default("RU"),
  /**
   * Which map draws the traveller's trips. Yandex by default: it works with no
   * key and covers Russia best. Google needs the server's key; without it the
   * web falls back to Yandex and says so.
   */
  mapEngine: z5.enum(MAP_ENGINES).default("yandex")
});
var User = z5.object({
  id: z5.string().uuid(),
  handle: Handle,
  displayName: z5.string().max(60).nullable().default(null),
  avatarEmoji: z5.string().max(8).nullable().default(null),
  role: UserRole.default("user"),
  preferences: UserPreferences,
  createdAt: IsoDateTime,
  lastSeenAt: IsoDateTime.nullable().default(null),
  tripCount: z5.number().int().nonnegative().default(0)
});
var PublicUser = z5.object({
  id: z5.string().uuid(),
  handle: Handle,
  displayName: z5.string().nullable(),
  avatarEmoji: z5.string().nullable()
});
var RegisterInput = z5.object({
  handle: Handle,
  password: Password,
  inviteCode: z5.string().trim().min(4).max(64),
  displayName: z5.string().trim().max(60).optional(),
  homeCountry: z5.string().length(2).toUpperCase().optional()
});
var LoginInput = z5.object({
  handle: Handle,
  password: z5.string().min(1).max(200)
});
var ChangePasswordInput = z5.object({
  currentPassword: z5.string().min(1),
  newPassword: Password
});
var RecoverInput = z5.object({
  handle: Handle,
  recoveryCode: z5.string().trim().min(8).max(64),
  newPassword: Password
});
var AuthTokens = z5.object({
  accessToken: z5.string(),
  expiresIn: z5.number().int().positive()
});
var AuthSession = z5.object({
  user: User,
  tokens: AuthTokens,
  /** Only returned once, at registration. Never stored in plaintext server-side. */
  recoveryCode: z5.string().optional(),
  /** Personal Clubhouse invites minted at registration (also listed under /auth/me/invites). */
  inviteCodes: z5.array(z5.string()).optional()
});

// src/schemas/place.ts
import { z as z6 } from "zod";
var PlaceTicket = z6.object({
  price: z6.string().max(120).nullable().default(null),
  status: z6.enum(["free", "official", "local", "included", "tour", "private", "none", "unknown", "estimated"]).default("unknown"),
  source: z6.string().max(200).nullable().default(null),
  note: z6.string().max(600).nullable().default(null),
  bookingUrl: z6.string().url().nullable().default(null)
});
var CAVEAT_KINDS = [
  "access",
  "hours",
  "tickets",
  "booking",
  "documents",
  "payment",
  "closure",
  "season",
  "crowds",
  "transport",
  "rules",
  "other"
];
var CaveatKind = z6.enum(CAVEAT_KINDS);
var PlaceCaveat = z6.object({
  kind: CaveatKind.catch("other"),
  text: z6.string().min(1).max(300),
  /** Without knowing it the visit fails or costs hours: a tour time, a closed day, a pass. */
  important: z6.boolean().default(false),
  /** The only times one gets in — a tour's start, a boat's departure. */
  times: z6.array(ClockTime).max(8).default([]),
  /** ISO weekdays (1 Monday … 7 Sunday) on which there is no getting in. */
  closedDays: z6.array(z6.number().int().min(1).max(7)).max(7).default([]),
  sources: z6.array(z6.object({ label: z6.string().min(1).max(120), url: z6.string().url() })).max(4).default([])
});
var REVIEW_CATEGORIES = ["taste", "service", "atmosphere", "value"];
var ReviewCategory = z6.enum(REVIEW_CATEGORIES);
var PlaceReview = z6.object({
  scores: z6.record(ReviewCategory, z6.number().int().min(1).max(5)).default({}),
  note: z6.string().max(1e3).nullable().default(null)
});
function reviewAverage(review) {
  const values = Object.values(review?.scores ?? {}).filter((v) => typeof v === "number");
  if (!values.length) return null;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length * 10) / 10;
}
var PlaceHours = z6.object({
  /** The week as printed: «пн-пт 10:00–19:00; сб,вс 11:00–18:00», «ежедневно, круглосуточно». */
  line: z6.string().min(1).max(300),
  /** ISO weekdays the card marks closed, where it says so separately from the line. */
  closedDays: z6.array(z6.number().int().min(1).max(7)).max(7).default([]),
  source: z6.enum(["yandex", "google"]).nullable().default(null),
  checkedAt: IsoDateTime
});
var PlaceBase = z6.object({
  id: z6.string().uuid(),
  tripId: z6.string().uuid(),
  name: z6.string().min(1).max(200),
  kind: PlaceKind.default("sight"),
  lat: Latitude.nullable().default(null),
  lng: Longitude.nullable().default(null),
  address: z6.string().max(400).nullable().default(null),
  description: z6.string().max(4e3).nullable().default(null),
  /** Why this place is on the route — the "sales pitch" shown in cards. */
  highlight: z6.string().max(600).nullable().default(null),
  /** Nice-to-have rather than a must-see; hidden behind a filter in the UI. */
  optional: z6.boolean().default(false),
  /** Free-form tags: "unesco", "sunset", "kid-friendly". Drives filters and AI context. */
  tags: z6.array(z6.string().max(40)).max(24).default([]),
  ticket: PlaceTicket.nullable().default(null),
  links: z6.array(ExternalLink).max(12).default([]),
  /** What visitors run into — see `PlaceCaveat`. Written by the API, public like the ticket. */
  caveats: z6.array(PlaceCaveat).max(12).default([]),
  /** Opening hours from the map card — see `PlaceHours`. */
  hours: PlaceHours.nullable().default(null),
  /** Minutes you should budget on site. Used by the day-plan feasibility check. */
  durationMinutes: z6.number().int().min(0).max(24 * 60).nullable().default(null),
  rating: z6.number().min(0).max(5).nullable().default(null),
  /** Owner-only field: what you actually paid / plan to pay here. */
  cost: Money.nullable().default(null),
  /** Owner-only free-form note. */
  privateNote: z6.string().max(2e3).nullable().default(null),
  /** Owner-only: when the travellers went there; null — not yet. */
  visitedAt: IsoDateTime.nullable().default(null),
  /** Owner-only: their scores for it. */
  review: PlaceReview.nullable().default(null),
  /** Manual ordering inside the trip's place list. */
  sortOrder: z6.number().int().default(0),
  /** Populated by the API for convenience; not stored on the row. */
  photoCount: z6.number().int().nonnegative().default(0),
  coverPhotoUrl: z6.string().nullable().default(null)
});
var Place = PlaceBase.merge(SyncMeta);
var PlaceCreateInput = PlaceBase.omit({
  tripId: true,
  photoCount: true,
  coverPhotoUrl: true
}).partial().required({ name: true }).extend({ id: z6.string().uuid().optional() });
var PlaceUpdateInput = PlaceCreateInput.partial().extend({
  /** Optimistic concurrency: the rev this edit was based on. */
  baseRev: z6.number().int().nonnegative().optional()
});
var PublicPlace = PlaceBase.omit({
  cost: true,
  privateNote: true,
  visitedAt: true,
  review: true
}).extend({
  updatedAt: IsoDateTime.optional()
});

// src/schemas/activity.ts
import { z as z7 } from "zod";
var ActivityBase = z7.object({
  id: z7.string().uuid(),
  tripId: z7.string().uuid(),
  dayId: z7.string().uuid(),
  kind: ActivityKind.default("note"),
  title: z7.string().min(1).max(300),
  description: z7.string().max(4e3).nullable().default(null),
  startTime: ClockTime.nullable().default(null),
  endTime: ClockTime.nullable().default(null),
  durationMinutes: z7.number().int().min(0).max(24 * 60).nullable().default(null),
  placeId: z7.string().uuid().nullable().default(null),
  reservationId: z7.string().uuid().nullable().default(null),
  /** Hidden from the public projection even when personal notes are shown. */
  isPrivate: z7.boolean().default(false),
  /** Owner may skip it without deleting — keeps AI suggestions non-destructive. */
  isOptional: z7.boolean().default(false),
  done: z7.boolean().default(false),
  cost: Money.nullable().default(null),
  sortOrder: z7.number().int().default(0)
});
var Activity = ActivityBase.merge(SyncMeta);
var ActivityCreateInput = ActivityBase.omit({ tripId: true, dayId: true }).partial().required({ title: true }).extend({ id: z7.string().uuid().optional() });
var ActivityUpdateInput = ActivityCreateInput.partial().extend({
  dayId: z7.string().uuid().optional(),
  baseRev: z7.number().int().nonnegative().optional()
});
var PublicActivity = ActivityBase.omit({
  cost: true,
  isPrivate: true,
  done: true,
  reservationId: true
});
var DayTimesInput = z7.object({
  times: z7.array(z7.object({ activityId: z7.string().uuid(), startTime: ClockTime })).min(1).max(60)
});

// src/schemas/day.ts
import { z as z8 } from "zod";
var TripDayBase = z8.object({
  id: z8.string().uuid(),
  tripId: z8.string().uuid(),
  dayIndex: z8.number().int().min(1),
  date: IsoDate.nullable().default(null),
  title: z8.string().max(200).nullable().default(null),
  summary: z8.string().max(2e3).nullable().default(null),
  /** "Олюдениз → Каш · ≈110 км / 2 ч" — human text, computed route lives in the map layer. */
  travelSummary: z8.string().max(400).nullable().default(null),
  /** Where you sleep this night. A Place of kind `lodging`. */
  basePlaceId: z8.string().uuid().nullable().default(null),
  /** Owner-only. */
  privateNote: z8.string().max(4e3).nullable().default(null),
  /** Accent colour inherited from the base, used by map + timeline. */
  color: z8.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null)
});
var TripDay = TripDayBase.merge(SyncMeta).extend({
  activities: z8.array(Activity).default([]),
  placeIds: z8.array(z8.string().uuid()).default([])
});
var TripDayCreateInput = TripDayBase.omit({ tripId: true }).partial().extend({ id: z8.string().uuid().optional() });
var TripDayUpdateInput = TripDayCreateInput.partial().extend({
  baseRev: z8.number().int().nonnegative().optional()
});
var PublicTripDay = TripDayBase.omit({ privateNote: true }).extend({
  activities: z8.array(PublicActivity).default([]),
  placeIds: z8.array(z8.string().uuid()).default([]),
  /** Present instead of `date` when exact dates are hidden: "начало июня". */
  dateLabel: z8.string().nullable().default(null)
});

// src/schemas/reservation.ts
import { z as z9 } from "zod";
var ReservationConfidential = z9.object({
  bookingRef: z9.string().max(200).nullable().default(null),
  confirmationNumber: z9.string().max(200).nullable().default(null),
  pinCode: z9.string().max(50).nullable().default(null),
  guestName: z9.string().max(200).nullable().default(null),
  passengers: z9.array(z9.string().max(120)).max(20).default([]),
  phone: z9.string().max(60).nullable().default(null),
  address: z9.string().max(400).nullable().default(null),
  supplierNote: z9.string().max(2e3).nullable().default(null)
});
var ReservationBase = z9.object({
  id: z9.string().uuid(),
  tripId: z9.string().uuid(),
  kind: ReservationKind.default("other"),
  title: z9.string().min(1).max(300),
  provider: z9.string().max(200).nullable().default(null),
  /** Free text so it works for "Внуково (VKO)" and "Sunmed Lodge" alike. */
  fromLabel: z9.string().max(200).nullable().default(null),
  toLabel: z9.string().max(200).nullable().default(null),
  startAt: IsoDateTime.nullable().default(null),
  endAt: IsoDateTime.nullable().default(null),
  startDate: IsoDate.nullable().default(null),
  endDate: IsoDate.nullable().default(null),
  /** Link to the map pin (hotel, rental office, airport). */
  placeId: z9.string().uuid().nullable().default(null),
  /** Which day of the trip this belongs to, when it is a point event. */
  dayId: z9.string().uuid().nullable().default(null),
  price: Money.nullable().default(null),
  /** Deep link to buy/book this — not personal data, safe to publish. */
  bookingUrl: z9.string().url().nullable().default(null),
  /** Whether `price` is a real quoted fare or a rough guess. Null = not set by AI at all. */
  priceAccuracy: z9.enum(["exact", "estimated"]).nullable().default(null),
  /** Publishable description: "Airbus A320 · 4 ч 30 мин · багаж 20 кг". */
  details: z9.string().max(4e3).nullable().default(null),
  /**
   * Whether this is paid for or only intended: `planned` is a fare the
   * traveller means to buy — a screenshot of a search result, not a ticket.
   * The plan is built around it all the same; the card says it is not bought.
   */
  status: ReservationStatus.default("booked"),
  confidential: ReservationConfidential.default({}),
  sortOrder: z9.number().int().default(0),
  /**
   * Set when the trip's deal was confirmed as bought: the booking is fixed —
   * dates, route, price — and only its references may still be filled in.
   * Server-owned: no input accepts it.
   */
  lockedAt: IsoDateTime.nullable().default(null)
});
var Reservation = ReservationBase.merge(SyncMeta);
var ReservationCreateInput = ReservationBase.omit({ tripId: true, lockedAt: true }).partial().required({ title: true }).extend({ id: z9.string().uuid().optional() });
var ReservationUpdateInput = ReservationCreateInput.partial().extend({
  baseRev: z9.number().int().nonnegative().optional()
});
var PublicReservation = z9.object({
  id: z9.string().uuid(),
  kind: ReservationKind,
  title: z9.string(),
  provider: z9.string().nullable(),
  fromLabel: z9.string().nullable(),
  toLabel: z9.string().nullable(),
  placeId: z9.string().uuid().nullable(),
  details: z9.string().nullable(),
  dateLabel: z9.string().nullable().default(null),
  bookingUrl: z9.string().url().nullable().default(null)
});

// src/schemas/document.ts
import { z as z10 } from "zod";
var TripDocumentBase = z10.object({
  id: z10.string().uuid(),
  tripId: z10.string().uuid(),
  title: z10.string().min(1).max(300),
  group: DocumentGroup.default("other"),
  fileKey: z10.string().min(1),
  mimeType: z10.string().max(120).default("application/pdf"),
  sizeBytes: z10.number().int().nonnegative().default(0),
  reservationId: z10.string().uuid().nullable().default(null),
  /** Bookings besides `reservationId` that this file also proves. */
  reservationIds: z10.array(z10.string().uuid()).default([]),
  dayId: z10.string().uuid().nullable().default(null),
  note: z10.string().max(1e3).nullable().default(null),
  sortOrder: z10.number().int().default(0),
  /** Populated on read: short-lived download URL. */
  url: z10.string().nullable().default(null)
});
var TripDocument = TripDocumentBase.merge(SyncMeta);
var TripDocumentUpdateInput = z10.object({
  title: z10.string().min(1).max(300),
  group: DocumentGroup,
  reservationId: z10.string().uuid().nullable(),
  dayId: z10.string().uuid().nullable(),
  note: z10.string().max(1e3).nullable(),
  sortOrder: z10.number().int()
}).partial();

// src/schemas/photo.ts
import { z as z11 } from "zod";
var PhotoAssignment = z11.enum(["auto_geo", "auto_time", "manual", "unassigned"]);
var PhotoBase = z11.object({
  id: z11.string().uuid(),
  tripId: z11.string().uuid(),
  placeId: z11.string().uuid().nullable().default(null),
  dayId: z11.string().uuid().nullable().default(null),
  /** Storage keys, resolved to URLs by the API. */
  fileKey: z11.string().min(1),
  thumbKey: z11.string().nullable().default(null),
  url: z11.string().nullable().default(null),
  thumbUrl: z11.string().nullable().default(null),
  mimeType: z11.string().max(120).default("image/jpeg"),
  sizeBytes: z11.number().int().nonnegative().default(0),
  width: z11.number().int().positive().nullable().default(null),
  height: z11.number().int().positive().nullable().default(null),
  takenAt: IsoDateTime.nullable().default(null),
  lat: Latitude.nullable().default(null),
  lng: Longitude.nullable().default(null),
  caption: z11.string().max(600).nullable().default(null),
  visibility: PhotoVisibility.default("trip"),
  assignment: PhotoAssignment.default("unassigned"),
  /** How far (metres) the photo was from the place it got attached to. */
  assignmentDistanceM: z11.number().nonnegative().nullable().default(null),
  /** EXIF was present but stripped from the stored file — we keep only what we need. */
  hadExifGps: z11.boolean().default(false),
  sortOrder: z11.number().int().default(0)
});
var Photo = PhotoBase.merge(SyncMeta);
var PhotoUpdateInput = z11.object({
  placeId: z11.string().uuid().nullable(),
  dayId: z11.string().uuid().nullable(),
  caption: z11.string().max(600).nullable(),
  visibility: PhotoVisibility,
  sortOrder: z11.number().int(),
  baseRev: z11.number().int().nonnegative()
}).partial();
var PhotoBulkUpdateInput = z11.object({
  photoIds: z11.array(z11.string().uuid()).min(1).max(500),
  placeId: z11.string().uuid().nullable().optional(),
  dayId: z11.string().uuid().nullable().optional(),
  visibility: PhotoVisibility.optional()
});
var PublicPhoto = PhotoBase.pick({
  id: true,
  placeId: true,
  dayId: true,
  url: true,
  thumbUrl: true,
  width: true,
  height: true,
  caption: true,
  sortOrder: true
});
var PhotoIngestResult = z11.object({
  uploaded: z11.number().int().nonnegative(),
  matched: z11.number().int().nonnegative(),
  unmatched: z11.number().int().nonnegative(),
  failed: z11.array(z11.object({ filename: z11.string(), reason: z11.string() })).default([]),
  photos: z11.array(Photo).default([])
});

// src/schemas/deals.ts
import { z as z12 } from "zod";
var DEAL_KINDS = ["tour", "flight", "train", "hotel", "other"];
var DealKind = z12.enum(DEAL_KINDS);
var DEAL_ORIGINS = ["channel", "site", "fares", "agent"];
var DealOrigin = z12.enum(DEAL_ORIGINS);
var DEAL_MEALS = ["RO", "BB", "HB", "FB", "AI"];
var DealMeal = z12.enum(DEAL_MEALS);
var DealDetails = z12.object({
  hotel: z12.object({
    name: z12.string().max(200).nullable().default(null),
    stars: z12.number().int().min(1).max(5).nullable().default(null),
    meal: z12.string().max(120).nullable().default(null),
    room: z12.string().max(200).nullable().default(null),
    address: z12.string().max(300).nullable().default(null),
    url: z12.string().url().nullable().default(null)
  }).nullable().default(null),
  flights: z12.array(
    z12.object({
      from: z12.string().max(120),
      to: z12.string().max(120),
      date: IsoDate.nullable().default(null),
      departTime: z12.string().max(10).nullable().default(null),
      arriveTime: z12.string().max(10).nullable().default(null),
      airline: z12.string().max(120).nullable().default(null),
      flightNumber: z12.string().max(20).nullable().default(null)
    })
  ).max(8).default([]),
  operator: z12.string().max(120).nullable().default(null),
  included: z12.array(z12.string().max(160)).max(12).default([]),
  notIncluded: z12.array(z12.string().max(160)).max(12).default([]),
  notes: z12.array(z12.string().max(300)).max(10).default([])
});
var DealVerification = z12.object({
  status: z12.enum(["confirmed", "changed", "unavailable", "unknown"]),
  checkedAt: IsoDateTime,
  /** The dates and party the check was made for. */
  dateFrom: IsoDate.nullable(),
  dateTo: IsoDate.nullable(),
  travelerCount: z12.number().int().min(1),
  pricePerPerson: z12.number().nullable(),
  totalPrice: z12.number().nullable(),
  /** Where it can be bought now, as found by the check. */
  url: z12.string().url().nullable(),
  note: z12.string().max(400).nullable()
});
var TravelDeal = z12.object({
  id: z12.string(),
  origin: DealOrigin,
  /** Channel username or site the deal was found on. */
  source: z12.string(),
  /** The post itself, so a reader can see what was promised. */
  sourceUrl: z12.string().url().nullable(),
  postedAt: IsoDateTime.nullable(),
  kind: DealKind,
  title: z12.string(),
  destinationCity: z12.string().nullable(),
  destinationCountry: z12.string().nullable(),
  originCities: z12.array(z12.string()),
  /** Exact dates for a tour; the window fares are on sale for, for a ticket. */
  dateFrom: IsoDate.nullable(),
  dateTo: IsoDate.nullable(),
  /** True when the dates are a sale window, not the dates of one trip. */
  flexibleDates: z12.boolean(),
  nights: z12.number().int().nullable(),
  price: z12.number().nullable(),
  currency: z12.string(),
  /** What the price buys: one person, the whole party, or one way. */
  pricePer: z12.enum(["person", "total", "oneway"]),
  roundTrip: z12.boolean().nullable(),
  /** Where to buy it or check it — the link the post gave. */
  url: z12.string().url(),
  /** The same link after its redirects: the actual tour or ticket page. */
  finalUrl: z12.string().url().nullable(),
  /** One or two sentences: why go there now. */
  summary: z12.string(),
  /** 1–10: how worthwhile the trip is, price aside. */
  interest: z12.number().min(0).max(10),
  details: DealDetails.nullable(),
  verification: DealVerification.nullable().default(null),
  /**
   * The hotel's stars and the board, from the details or the deal's own
   * words — what the filters go by. Null where nothing says, which is often:
   * a channel post may name neither. Always null for a ticket.
   */
  stars: z12.number().int().min(1).max(5).nullable().default(null),
  meal: DealMeal.nullable().default(null)
});
var DealSearchRequest = z12.object({
  originCountry: z12.string().length(2).default("RU"),
  originCity: z12.string().min(2).max(120),
  /** Where to, in words: «Сочи», «Египет». Narrows the deals and aims the agent. */
  q: z12.string().max(80).optional(),
  dateFrom: IsoDate,
  dateTo: IsoDate,
  /** Days either side of the dates a deal may still start or end. */
  flexDays: z12.number().int().min(0).max(7).default(2),
  travelerCount: z12.number().int().min(1).max(10).default(2),
  /** Only trips at most this much per person in all — tickets and stay — in roubles. */
  maxPrice: z12.number().positive().optional(),
  kinds: z12.array(DealKind).max(4).optional(),
  /**
   * Search the tour and ticket sites with an agent when the channels have too
   * little for these dates. On by default; it takes a minute or two.
   */
  agentSearch: z12.boolean().default(true)
});
var CostPart = z12.object({
  amount: z12.number(),
  /** `deal` — the deal's own price; `fare` — a real cached fare; `estimate` — a model's estimate. */
  source: z12.enum(["deal", "fare", "estimate"]),
  note: z12.string().max(200).nullable().default(null),
  url: z12.string().url().nullable().default(null)
});
var DealSearchResult = z12.object({
  deal: TravelDeal,
  /** How well the deal fits the request: exact dates, a window, a near miss. */
  fit: z12.enum(["exact", "window", "near"]),
  /**
   * The whole trip for the party, in roubles: a tour's price, or a fare's
   * tickets both ways plus somewhere to stay. Null while it cannot be told.
   */
  totalPrice: z12.number().nullable(),
  /** The same per person — what «до, ₽ с человека» filters on. */
  perPerson: z12.number().nullable(),
  /** What the total is made of: tickets, the way back, the stay. */
  breakdown: z12.object({
    tickets: CostPart.nullable(),
    returnTicket: CostPart.nullable(),
    lodging: CostPart.nullable()
  }),
  /** Some of the total is an estimate rather than a price someone quoted. */
  estimated: z12.boolean(),
  /** Its stay or return fare is still being estimated. */
  pending: z12.boolean(),
  /**
   * How interesting the trip is, 0–10, regardless of price: the place's own
   * rating, moved a little by the hotel and the length — and lowered for the
   * same place again further down the list. What «Интереснее» orders by.
   */
  interest: z12.number()
});
var DealSearchStatus = z12.enum(["running", "done", "failed"]);
var DealSearchResponse = z12.object({
  id: z12.string().uuid(),
  request: DealSearchRequest,
  results: z12.array(DealSearchResult),
  /** The agent's search: not asked for, still going, finished, or failed. */
  agent: z12.object({
    status: z12.enum(["skipped", "running", "done", "failed"]),
    error: z12.string().nullable(),
    found: z12.number().int()
  }),
  status: DealSearchStatus,
  /** Deals an agent is checking against their pages right now. */
  verifying: z12.array(z12.string()),
  /** Stays and return fares still being estimated. */
  estimating: z12.boolean(),
  /** When the channels were last read. */
  channelsUpdatedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime
});
var DealPlanRequest = z12.object({
  searchId: z12.string().uuid(),
  dealId: z12.string(),
  pace: z12.enum(["relaxed", "balanced", "packed"]).optional()
});
var TripDeal = z12.object({
  dealId: z12.string(),
  kind: DealKind,
  title: z12.string(),
  source: z12.string(),
  sourceUrl: z12.string().url().nullable(),
  url: z12.string().url(),
  finalUrl: z12.string().url().nullable(),
  price: z12.number().nullable(),
  currency: z12.string(),
  pricePer: z12.enum(["person", "total", "oneway"]),
  totalPrice: z12.number().nullable(),
  dateFrom: IsoDate.nullable(),
  dateTo: IsoDate.nullable(),
  summary: z12.string(),
  details: DealDetails.nullable(),
  /** The last check of the deal on its pages, as of when the trip was made. */
  verification: DealVerification.nullable().default(null),
  /** Still assembling hotel and flight details from the deal's pages. */
  enriching: z12.boolean().default(false),
  /** The bookings created from the deal — the ones confirming it fixes. */
  reservationIds: z12.array(z12.string().uuid()).max(20).default([]),
  /** Party size the total was worked out for. */
  travelerCount: z12.number().int().min(1).default(1),
  confirmed: z12.boolean().default(false),
  confirmedAt: IsoDateTime.nullable().default(null)
});
var DealConfirmInput = z12.object({ confirmed: z12.boolean() });

// src/schemas/trip.ts
import { z as z13 } from "zod";
var Slider = z13.number().int().min(1).max(5);
var TripPreferences = z13.object({
  /** 1 бюджетно · 5 премиум */
  budget: Slider.optional(),
  /** 1 спокойный отдых · 5 активный */
  activity: Slider.optional(),
  /** 1 групповые туры · 5 индивидуальные поездки */
  social: Slider.optional(),
  /** 1 город · 5 природа */
  nature: Slider.optional(),
  /** 1 известные места · 5 off-the-beaten-path */
  novelty: Slider.optional(),
  /** 1 простое жильё · 5 максимум комфорта */
  comfort: Slider.optional(),
  carRental: z13.boolean().optional(),
  nightTransfers: z13.boolean().optional(),
  withKids: z13.boolean().optional(),
  accessible: z13.boolean().optional(),
  vegetarian: z13.boolean().optional(),
  withPet: z13.boolean().optional(),
  /** Which map services to link places to. Empty = default (google + yandex). */
  mapProviders: z13.array(z13.enum(["travessa", "google", "yandex", "osm"])).max(4).optional(),
  /** How the traveller gets around; feeds city-aware transfer planning. */
  transport: z13.array(z13.enum(["public", "taxi", "rental", "own"])).max(4).optional()
}).default({});
var VisaRequirement = z13.object({
  /** Traveller passport, ISO-3166-1 alpha-2. */
  citizenship: z13.string().length(2),
  /** Destination country, ISO-3166-1 alpha-2. */
  destination: z13.string().length(2),
  destinationName: z13.string().max(120).nullable().default(null),
  regime: z13.enum(["visa_free", "visa_on_arrival", "e_visa", "eta", "embassy", "banned", "unknown"]).default("unknown"),
  summary: z13.string().max(800).nullable().default(null),
  /** How long the visa itself takes to issue. */
  processingTime: z13.string().max(160).nullable().default(null),
  /** How far ahead to start — the number the traveller actually plans around. */
  leadTime: z13.string().max(160).nullable().default(null),
  /** Permitted stay, e.g. "до 90 дней в течение 180". */
  stayDuration: z13.string().max(160).nullable().default(null),
  cost: z13.string().max(160).nullable().default(null),
  /** Rough approval odds, kept as free text ("~95%") — never a false precision. */
  approvalRate: z13.string().max(80).nullable().default(null),
  documents: z13.array(z13.string().max(240)).max(24).default([]),
  sources: z13.array(z13.string().max(300)).max(8).default([]),
  /** True once at least one official source backed the entry. */
  verified: z13.boolean().default(false)
});
var VisaInfo = z13.object({
  generatedAt: IsoDateTime,
  model: z13.string().max(120).nullable().default(null),
  citizenships: z13.array(z13.string().length(2)).max(5).default([]),
  requirements: z13.array(VisaRequirement).max(60).default([]),
  disclaimer: z13.string().max(800).nullable().default(null)
});
var TripBase = z13.object({
  id: z13.string().uuid(),
  ownerId: z13.string().uuid(),
  slug: z13.string().min(1).max(80),
  title: z13.string().min(1).max(200),
  subtitle: z13.string().max(300).nullable().default(null),
  summary: z13.string().max(6e3).nullable().default(null),
  coverUrl: z13.string().nullable().default(null),
  /** Accent used across the trip's UI; picked from the cover or set by hand. */
  accentColor: z13.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null),
  status: TripStatus.default("draft"),
  visibility: TripVisibility.default("private"),
  privacy: PrivacyProfile,
  startDate: IsoDate.nullable().default(null),
  endDate: IsoDate.nullable().default(null),
  nights: z13.number().int().min(0).max(400).nullable().default(null),
  /**
   * Where the traveller sets off from. Needed for two concrete things: ticket
   * searches that are pre-filled rather than generic, and a drive estimate for
   * someone taking their own car.
   */
  originCity: z13.string().max(120).nullable().default(null),
  originCountry: z13.string().length(2).toUpperCase().nullable().default(null),
  /** ISO-3166-1 alpha-2, e.g. ["TR"]. Drives the gallery filters. */
  countries: z13.array(z13.string().length(2).toUpperCase()).max(20).default([]),
  regions: z13.array(z13.string().max(80)).max(40).default([]),
  travelerCount: z13.number().int().min(1).max(50).default(1),
  /** Owner-only: who is going. Public projection shows this only if allowed. */
  travelers: z13.array(z13.string().max(120)).max(50).default([]),
  pace: TripPace.default("balanced"),
  styles: z13.array(TravelStyle).max(11).default([]),
  currency: z13.string().length(3).default("EUR"),
  budget: Money.nullable().default(null),
  tags: z13.array(z13.string().max(40)).max(24).default([]),
  language: z13.enum(["ru", "en"]).default("ru"),
  /** Provenance when the trip was copied from someone else's. */
  forkedFromId: z13.string().uuid().nullable().default(null),
  forkCount: z13.number().int().nonnegative().default(0),
  viewCount: z13.number().int().nonnegative().default(0),
  /** Curated demo trips shown on the landing page. Admin-set. */
  isFeatured: z13.boolean().default(false),
  publishedAt: IsoDateTime.nullable().default(null),
  /** Set when the traveller marks the trip done; it stays editable afterwards. */
  completedAt: IsoDateTime.nullable().default(null),
  /** Planning knobs. Owner-only — never picked into PublicTrip. */
  preferences: TripPreferences,
  /** Traveller citizenships (ISO2) for visa guidance. Owner-only. */
  citizenships: z13.array(z13.string().length(2).toUpperCase()).max(5).default([]),
  /** Generated visa/entry guidance. Owner-only. */
  visaInfo: VisaInfo.nullable().default(null),
  /** The tour or fare deal the trip was built from. Owner-only. */
  deal: TripDeal.nullable().default(null)
});
var Trip = TripBase.merge(SyncMeta);
var TripSummary = TripBase.pick({
  id: true,
  slug: true,
  title: true,
  subtitle: true,
  coverUrl: true,
  accentColor: true,
  status: true,
  visibility: true,
  startDate: true,
  endDate: true,
  nights: true,
  countries: true,
  regions: true,
  styles: true,
  pace: true,
  tags: true,
  forkCount: true,
  viewCount: true,
  isFeatured: true,
  completedAt: true
}).extend({
  owner: PublicUser.nullable().default(null),
  dayCount: z13.number().int().nonnegative().default(0),
  placeCount: z13.number().int().nonnegative().default(0),
  photoCount: z13.number().int().nonnegative().default(0),
  updatedAt: IsoDateTime,
  /** Present only in the owner's own listing. */
  isOwner: z13.boolean().default(false),
  /**
   * The caller's own standing on the trip in "my trips": `owner`, or the role
   * an invite or the owner gave them. Null in the gallery.
   */
  myRole: z13.enum(["owner", "editor", "viewer"]).nullable().default(null)
});
var TripDetail = Trip.extend({
  owner: PublicUser.nullable().default(null),
  days: z13.array(TripDay).default([]),
  places: z13.array(Place).default([]),
  reservations: z13.array(Reservation).default([]),
  documents: z13.array(TripDocument).default([]),
  photos: z13.array(Photo).default([]),
  shareUrl: z13.string().nullable().default(null),
  /** How many people per-person prices are paid for — see `partySize()`. Private, like every price. */
  partySize: z13.number().int().min(1).max(50).default(1)
});
var PublicTrip = TripBase.pick({
  id: true,
  slug: true,
  title: true,
  subtitle: true,
  summary: true,
  coverUrl: true,
  accentColor: true,
  status: true,
  nights: true,
  countries: true,
  regions: true,
  travelerCount: true,
  pace: true,
  styles: true,
  tags: true,
  language: true,
  forkCount: true,
  viewCount: true,
  isFeatured: true,
  completedAt: true
}).extend({
  owner: PublicUser.nullable().default(null),
  /** Formatted per privacy: real dates, or "июнь · 11 ночей". */
  dateLabel: z13.string().nullable().default(null),
  startDate: IsoDate.nullable().default(null),
  endDate: IsoDate.nullable().default(null),
  travelers: z13.array(z13.string()).default([]),
  days: z13.array(PublicTripDay).default([]),
  places: z13.array(PublicPlace).default([]),
  reservations: z13.array(PublicReservation).default([]),
  photos: z13.array(PublicPhoto).default([]),
  budget: Money.nullable().default(null),
  allowForking: z13.boolean().default(true),
  showMap: z13.boolean().default(true),
  updatedAt: IsoDateTime
});
var TripCreateInput = z13.object({
  id: z13.string().uuid().optional(),
  title: z13.string().min(1).max(200),
  subtitle: z13.string().max(300).optional(),
  summary: z13.string().max(6e3).optional(),
  startDate: IsoDate.optional(),
  endDate: IsoDate.optional(),
  nights: z13.number().int().min(0).max(400).optional(),
  /** Where the traveller sets off from — drives ticket links and drive time. */
  originCity: z13.string().max(120).optional(),
  originCountry: z13.string().length(2).optional(),
  countries: z13.array(z13.string().length(2)).max(20).optional(),
  regions: z13.array(z13.string().max(80)).max(40).optional(),
  travelerCount: z13.number().int().min(1).max(50).optional(),
  pace: TripPace.optional(),
  styles: z13.array(TravelStyle).max(11).optional(),
  currency: z13.string().length(3).optional(),
  visibility: TripVisibility.optional(),
  language: z13.enum(["ru", "en"]).optional(),
  tags: z13.array(z13.string().max(40)).max(24).optional(),
  preferences: TripPreferences.optional(),
  citizenships: z13.array(z13.string().length(2)).max(5).optional(),
  /** Target budget for the whole trip — feeds the AI planner's realism check. */
  budget: Money.optional()
});
var TripUpdateInput = TripCreateInput.omit({ id: true }).partial().extend({
  status: TripStatus.optional(),
  /** null reopens a completed trip; a date/true marks it done (route sets the time). */
  completed: z13.boolean().optional(),
  privacy: PrivacyProfile.partial().optional(),
  accentColor: z13.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  coverUrl: z13.string().nullable().optional(),
  travelers: z13.array(z13.string().max(120)).max(50).optional(),
  budget: Money.nullable().optional(),
  baseRev: z13.number().int().nonnegative().optional()
});
var TripForkInput = z13.object({
  title: z13.string().min(1).max(200).optional(),
  /** Shift the copied itinerary to new dates; days keep their relative order. */
  startDate: IsoDate.optional(),
  includePhotos: z13.boolean().default(false),
  includeFood: z13.boolean().default(true),
  includeOptional: z13.boolean().default(true)
});
var TripListQuery = z13.object({
  q: z13.string().max(120).optional(),
  country: z13.string().length(2).optional(),
  style: TravelStyle.optional(),
  pace: TripPace.optional(),
  featured: z13.coerce.boolean().optional(),
  /** Only trips not over by this date — with `startsBy`, the dashboard's "right now". */
  endsFrom: IsoDate.optional(),
  /** Only trips starting on or before this date. */
  startsBy: IsoDate.optional(),
  sort: z13.enum(["recent", "popular", "longest"]).default("recent"),
  cursor: z13.string().optional(),
  limit: z13.coerce.number().int().min(1).max(50).default(12)
});
var TripShare = z13.object({
  id: z13.string().uuid(),
  tripId: z13.string().uuid(),
  token: z13.string(),
  url: z13.string(),
  label: z13.string().nullable().default(null),
  expiresAt: IsoDateTime.nullable().default(null),
  revokedAt: IsoDateTime.nullable().default(null),
  viewCount: z13.number().int().nonnegative().default(0),
  createdAt: IsoDateTime
});
var TripMemberRow = z13.object({
  userId: z13.string().uuid(),
  handle: z13.string(),
  displayName: z13.string().nullable().default(null),
  avatarEmoji: z13.string().nullable().default(null),
  /** `editor` may change the trip; `viewer` may read the private version. */
  role: z13.enum(["editor", "viewer"]),
  createdAt: IsoDateTime
});
var TripMemberCreateInput = z13.object({
  handle: z13.string().min(3).max(32),
  role: z13.enum(["editor", "viewer"]).default("editor")
});
var TripMemberUpdateInput = z13.object({
  role: z13.enum(["editor", "viewer"])
});
var TripInviteRow = z13.object({
  id: z13.string().uuid(),
  tripId: z13.string().uuid(),
  token: z13.string(),
  url: z13.string(),
  role: z13.enum(["editor", "viewer"]),
  expiresAt: IsoDateTime.nullable().default(null),
  usedCount: z13.number().int().nonnegative().default(0),
  createdAt: IsoDateTime
});
var TripInviteCreateInput = z13.object({
  role: z13.enum(["editor", "viewer"]).default("editor"),
  expiresInDays: z13.number().int().min(1).max(365).optional()
});
var TripInvitePreview = z13.object({
  tripId: z13.string().uuid(),
  title: z13.string(),
  subtitle: z13.string().nullable().default(null),
  coverUrl: z13.string().nullable().default(null),
  countries: z13.array(z13.string()).default([]),
  owner: PublicUser.nullable().default(null),
  role: z13.enum(["editor", "viewer"]),
  expiresAt: IsoDateTime.nullable().default(null),
  /** The signed-in caller's current role on the trip, if any. */
  myRole: z13.enum(["owner", "editor", "viewer"]).nullable().default(null)
});
var TripInviteAccepted = z13.object({
  tripId: z13.string().uuid(),
  role: z13.enum(["owner", "editor", "viewer"])
});
var TripShareCreateInput = z13.object({
  label: z13.string().max(120).optional(),
  expiresInDays: z13.number().int().min(1).max(3650).optional()
});
var TripChangeEntry = z13.object({
  id: z13.string().uuid(),
  at: z13.string(),
  /** Who: a person, or null for the system. With `source: 'ai'`, the person the AI worked for. */
  author: z13.object({ id: z13.string().uuid(), handle: z13.string(), displayName: z13.string().nullable() }).nullable(),
  source: z13.enum(["user", "ai", "system"]),
  jobId: z13.string().uuid().nullable(),
  entity: z13.enum(["trip", "day", "activity", "place", "reservation", "document", "photo"]),
  entityId: z13.string().uuid(),
  action: z13.enum(["create", "update", "delete", "restore"]),
  label: z13.string(),
  context: z13.string().nullable(),
  /** before/after null where the value is owner-only or was not known. */
  fields: z13.array(z13.object({ field: z13.string(), before: z13.unknown(), after: z13.unknown() }))
});

// src/schemas/ai.ts
import { z as z14 } from "zod";
var AiPlaceDraft = z14.object({
  name: z14.string().min(1).max(200),
  kind: PlaceKind.default("sight"),
  lat: Latitude.nullable().default(null),
  lng: Longitude.nullable().default(null),
  /** Real postal/OSM address, filled in by the geocoding verifier — not by the model. */
  address: z14.string().max(300).nullable().default(null),
  description: z14.string().max(1200).nullable().default(null),
  /**
   * Why this place earns its slot — the justification shown on the card. The
   * research agent rewrites it from the evidence it actually found, so it is a
   * grounded argument ("the only surviving Lycian rock tomb you can walk into"),
   * not the model's enthusiasm.
   */
  highlight: z14.string().max(600).nullable().default(null),
  durationMinutes: z14.number().int().min(0).max(1440).nullable().default(null),
  optional: z14.boolean().default(false),
  tags: z14.array(z14.string().max(40)).max(12).default([]),
  /** Practical facts (price, hours) gathered by the research agent, with a source. */
  ticket: PlaceTicket.nullable().default(null),
  /** Numeric price for the budget total — sourced when possible, else a labelled estimate (see `ticket.status`). */
  cost: Money.nullable().default(null),
  /** Reference links (official site, Wikipedia, sources) — filled by the verifier. */
  links: z14.array(ExternalLink).max(8).default([]),
  /** What visitors run into, from reviews and forums — filled by the API, never by the model's memory. */
  caveats: z14.array(PlaceCaveat).max(12).default([]),
  /** The nuances were looked for (found or not), so the background sweep need not look again. */
  caveatsChecked: z14.boolean().default(false),
  /** Out of five, where a source rates the place (the food guide's Yandex rating). */
  rating: z14.number().min(0).max(5).nullable().default(null),
  /**
   * Grounding status, set by the verifier after generation — never by the model.
   * `verified` means at least one hard source (OpenStreetMap, Wikipedia) or a
   * corroborated web search confirmed the place exists; coordinates are snapped
   * to the real feature where available. `unverified` means it could not be
   * confirmed and needs a human to check it. `unchecked` means verification did
   * not run (disabled, or over the per-plan budget).
   */
  verification: z14.enum(["verified", "unverified", "unchecked"]).default("unchecked")
});
var AiActivityDraft = z14.object({
  kind: ActivityKind.default("visit"),
  title: z14.string().min(1).max(300),
  description: z14.string().max(1200).nullable().default(null),
  startTime: z14.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().default(null),
  durationMinutes: z14.number().int().min(0).max(1440).nullable().default(null),
  /** Index into the draft's `places` array — the model cannot invent UUIDs. */
  placeRef: z14.number().int().min(0).nullable().default(null),
  optional: z14.boolean().default(false)
});
var AiDayDraft = z14.object({
  dayIndex: z14.number().int().min(1),
  title: z14.string().max(200).nullable().default(null),
  summary: z14.string().max(1200).nullable().default(null),
  travelSummary: z14.string().max(300).nullable().default(null),
  /** Index into `places` of the lodging for this night. */
  basePlaceRef: z14.number().int().min(0).nullable().default(null),
  activities: z14.array(AiActivityDraft).max(20).default([])
});
var AiReservationDraft = z14.object({
  kind: ReservationKind.default("other"),
  title: z14.string().min(1).max(300),
  provider: z14.string().max(200).nullable().default(null),
  fromLabel: z14.string().max(200).nullable().default(null),
  toLabel: z14.string().max(200).nullable().default(null),
  startDate: IsoDate.nullable().default(null),
  endDate: IsoDate.nullable().default(null),
  /**
   * Local wall-clock times on `startDate` / `endDate`, when the source shows
   * them — a ticket's 07:10 departure and 08:40 landing. The day's plan starts
   * from the landing, so a date alone loses the one fact it needs.
   */
  startTime: z14.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().catch(null).default(null),
  endTime: z14.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().catch(null).default(null),
  /** `planned` for an offer the traveller has not bought yet — see `Reservation.status`. */
  status: ReservationStatus.catch("booked").default("booked"),
  /** Booking/confirmation reference — stored owner-only, never published. */
  bookingRef: z14.string().max(200).nullable().default(null),
  guestName: z14.string().max(200).nullable().default(null),
  details: z14.string().max(2e3).nullable().default(null),
  price: Money.nullable().default(null),
  /** Deep link to buy/book this — not personal data, safe to publish. */
  bookingUrl: z14.string().url().nullable().default(null),
  /** Whether `price` is a real quoted fare or a rough guess. */
  priceAccuracy: z14.enum(["exact", "estimated"]).nullable().default(null)
});
var AiTripDraft = z14.object({
  title: z14.string().min(1).max(200),
  subtitle: z14.string().max(300).nullable().default(null),
  summary: z14.string().max(4e3).nullable().default(null),
  countries: z14.array(z14.string().length(2)).max(10).default([]),
  regions: z14.array(z14.string().max(80)).max(20).default([]),
  nights: z14.number().int().min(0).max(400).nullable().default(null),
  pace: TripPace.default("balanced"),
  styles: z14.array(TravelStyle).max(6).default([]),
  places: z14.array(AiPlaceDraft).max(120).default([]),
  days: z14.array(AiDayDraft).max(60).default([]),
  /** Flight legs found for the trip: outbound, return, and any internal transfers. */
  reservations: z14.array(AiReservationDraft).max(20).default([]),
  /**
   * Model's own caveats — surfaced to the user rather than silently applied.
   *
   * Wider than the 400 characters a written caveat needs, because the pipeline
   * adds notes of its own after the model is done and two of them carry links:
   * «Дорога Москва → Ханья» with two search URLs came to 443 characters, and
   * percent-encoded Cyrillic in a query string is most of that. The draft was
   * then stored unvalidated and refused by this very schema when anyone tried
   * to apply it — a Crete plan that could be generated but not turned into a
   * trip. Twelve entries for the same reason: the generator already slices to
   * twelve, and grounding warnings must not push a visa note off the end.
   */
  notes: z14.array(z14.string().max(1200)).max(12).default([])
});
var AiBookingContext = z14.object({
  kind: z14.string().max(40),
  title: z14.string().max(300),
  provider: z14.string().max(200).nullable().default(null),
  fromLabel: z14.string().max(200).nullable().default(null),
  toLabel: z14.string().max(200).nullable().default(null),
  startDate: IsoDate.nullable().default(null),
  endDate: IsoDate.nullable().default(null),
  /**
   * When it actually happens, to the minute, when the reservation says so.
   *
   * The date alone was not enough and the gap showed: a flight landing at
   * 22:30 was passed to the planner as «2026-08-24», so the plan opened that
   * day with a 12:00 airport transfer and filled an afternoon the traveller
   * spent in the air. `Reservation.startAt` has carried the time all along —
   * it simply stopped here.
   */
  startAt: IsoDateTime.nullable().default(null),
  endAt: IsoDateTime.nullable().default(null),
  /** Free-text details as extracted — flight number, room type, times. */
  details: z14.string().max(600).nullable().default(null),
  /** Not bought yet: the plan is built around it all the same. */
  status: ReservationStatus.default("booked")
});
var AiPlanRequest = z14.object({
  prompt: z14.string().min(3).max(4e3),
  destination: z14.string().max(200).optional(),
  /** Where the traveller sets off from — tickets and drive time depend on it. */
  originCity: z14.string().max(120).optional(),
  originCountry: z14.string().length(2).optional(),
  originLat: Latitude.optional(),
  originLng: Longitude.optional(),
  startDate: IsoDate.optional(),
  nights: z14.number().int().min(1).max(90).optional(),
  travelerCount: z14.number().int().min(1).max(50).optional(),
  pace: TripPace.optional(),
  styles: z14.array(TravelStyle).max(6).optional(),
  budgetLevel: z14.enum(["budget", "mid", "premium"]).optional(),
  /** Target budget for the whole trip — checked against the generated total. */
  budget: Money.optional(),
  /**
   * The currency every price in this plan should be expressed in.
   *
   * Separate from `budget.currency` because a budget is optional and this is
   * not: the generator used to read the currency off the budget and fall back
   * to EUR, so an account set to roubles that did not type a target amount got
   * an itinerary priced in euro — and a budget panel adding up "263 $ + 83 €".
   */
  currency: z14.string().length(3).optional(),
  /** Sliders/switches that shape the plan. */
  preferences: TripPreferences.optional(),
  /** Traveller citizenships (ISO2) — used only to caveat visa-sensitive routing. */
  citizenships: z14.array(z14.string().length(2)).max(5).optional(),
  language: z14.enum(["ru", "en"]).default("ru"),
  /** Apply straight away, or return the draft for review first. */
  mode: z14.enum(["draft", "apply"]).default("draft"),
  /** Fill only what is empty; never overwrite what the user wrote by hand. */
  fillOnlyEmpty: z14.boolean().default(true),
  model: z14.string().max(120).optional(),
  /**
   * Create the trip and the job, but do not start generating yet.
   *
   * The create dialog uses this to get a trip to attach ticket and voucher
   * files to. A plan that ignores a flight the traveller has already booked is
   * worse than one that waits a minute for it to be read.
   */
  deferStart: z14.boolean().default(false),
  /**
   * Bookings the traveller already holds, filled in server-side from the
   * trip's reservations when the job starts. Never sent by a client.
   *
   * Note what is absent: no booking reference, no passenger name, no anything
   * from `Reservation.confidential`. This block is put in a prompt and sent to
   * a third-party model, and the planner needs to know *when* a flight lands,
   * never who is on it.
   */
  bookings: z14.array(AiBookingContext).max(40).optional()
});
var AiIngestRequest = z14.object({
  text: z14.string().min(3).max(2e4),
  mode: z14.enum(["draft", "apply"]).default("apply"),
  model: z14.string().max(120).optional()
});
var AiIngestResult = z14.object({
  summary: z14.string().max(600).nullable().default(null),
  reservations: z14.array(AiReservationDraft).max(30).default([]),
  places: z14.array(AiPlaceDraft).max(30).default([]),
  notes: z14.array(z14.string().max(400)).max(10).default([])
});
var AiParseDocumentsRequest = z14.object({
  documentIds: z14.array(z14.string().uuid()).min(1).max(20),
  /**
   * The ones the traveller marked as «планирую купить»: a screenshot of a
   * fare they mean to buy. What they produce is `status: 'planned'`.
   */
  plannedIds: z14.array(z14.string().uuid()).max(20).default([]),
  /** Marked «куплено»: booked whatever the screenshot looks like. Unmarked ones are judged by the reader. */
  bookedIds: z14.array(z14.string().uuid()).max(20).default([])
});
var AiParsedDocument = z14.object({
  documentId: z14.string().uuid(),
  filename: z14.string().max(300),
  summary: z14.string().max(600).nullable().default(null),
  createdReservations: z14.number().int().nonnegative().default(0),
  createdPlaces: z14.number().int().nonnegative().default(0),
  /**
   * The booking this document was filed against — an existing one it matched
   * («Volkswagen Polo», already in the trip) or the one it created.
   */
  attachedTo: z14.string().max(300).nullable().default(null),
  /** True when it matched a booking the trip already had, rather than adding one. */
  matched: z14.boolean().default(false),
  /** Why nothing came of it, in words a traveller can act on. */
  reason: z14.string().max(400).nullable().default(null),
  notes: z14.array(z14.string().max(400)).max(10).default([])
});
var AiParseDocumentsResult = z14.object({
  items: z14.array(AiParsedDocument).default([]),
  createdReservations: z14.number().int().nonnegative().default(0),
  /** Bookings the trip already had that a document filled in and was filed against. */
  updatedReservations: z14.number().int().nonnegative().default(0),
  createdPlaces: z14.number().int().nonnegative().default(0)
});
var AiVisaRequest = z14.object({
  /** Override the trip's stored citizenships; defaults to them when omitted. */
  citizenships: z14.array(z14.string().length(2)).min(1).max(5).optional(),
  /** Override destinations (ISO2); defaults to the trip's countries. */
  destinations: z14.array(z14.string().length(2)).min(1).max(20).optional(),
  language: z14.enum(["ru", "en"]).default("ru")
});
var AiEditRequest = z14.object({
  prompt: z14.string().min(3).max(4e3),
  scope: z14.enum(["trip", "day", "place"]).default("trip"),
  dayId: z14.string().uuid().optional(),
  placeId: z14.string().uuid().optional(),
  mode: z14.enum(["draft", "apply"]).default("draft"),
  model: z14.string().max(120).optional()
});
var patchDate = z14.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null);
var patchDayTitle = z14.string().max(200).nullable().default(null);
var patchTime = z14.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().default(null);
var PatchActivity = AiActivityDraft.extend({
  placeName: z14.string().max(200).nullable().default(null)
});
var AiPatchOp = z14.discriminatedUnion("op", [
  z14.object({
    op: z14.literal("add_place"),
    place: AiPlaceDraft,
    /** Set: also drop a visit into this day. Null: the place stays on the map only. */
    dayIndex: z14.number().int().min(1).nullable().default(null),
    date: patchDate,
    dayTitle: patchDayTitle,
    /** When the visit starts. Null: slotted by time, which puts an untimed item last. */
    startTime: patchTime,
    /** Town or district, so geocoding can tell the place from a namesake. */
    area: z14.string().max(160).nullable().default(null)
  }),
  z14.object({
    op: z14.literal("add_activity"),
    dayIndex: z14.number().int().min(1),
    date: patchDate,
    dayTitle: patchDayTitle,
    /** Town, when this visit names a place that is not on the map yet. */
    area: z14.string().max(160).nullable().default(null),
    activity: PatchActivity
  }),
  z14.object({
    op: z14.literal("update_day"),
    dayIndex: z14.number().int().min(1),
    date: patchDate,
    dayTitle: patchDayTitle,
    title: z14.string().max(200).nullable().default(null),
    summary: z14.string().max(1200).nullable().default(null)
  }),
  z14.object({
    op: z14.literal("remove_activity"),
    dayIndex: z14.number().int().min(1),
    date: patchDate,
    dayTitle: patchDayTitle,
    activityTitle: z14.string().max(300)
  }),
  z14.object({
    op: z14.literal("move_activity"),
    activityTitle: z14.string().min(1).max(300),
    fromDayIndex: z14.number().int().min(1),
    fromDate: patchDate,
    fromDayTitle: patchDayTitle,
    dayIndex: z14.number().int().min(1),
    date: patchDate,
    dayTitle: patchDayTitle,
    startTime: patchTime
  }),
  z14.object({
    /**
     * Change an item where it stands: «по желанию», its time, length, name or
     * text. Null leaves a field as it is. Without this op a model asked to mark
     * an item optional removed it and added a copy — losing the traveller's
     * «отмечено», the place link and the history of the row.
     */
    op: z14.literal("update_activity"),
    dayIndex: z14.number().int().min(1),
    date: patchDate,
    dayTitle: patchDayTitle,
    activityTitle: z14.string().min(1).max(300),
    optional: z14.boolean().nullable().default(null),
    startTime: patchTime,
    durationMinutes: z14.number().int().min(0).max(24 * 60).nullable().default(null),
    title: z14.string().min(1).max(300).nullable().default(null),
    description: z14.string().max(2e3).nullable().default(null)
  }),
  z14.object({
    /**
     * Put a whole day's plan into another day's place; the days between shift
     * by one. Dates, the night's lodging and bookings stay with the calendar
     * slot — only the plan moves. Several in one patch run in order, each on
     * the result of the one before, so a swap of 3 and 5 is «5 → 3», then
     * «3 → 5». Day references name the days as the plan listed them.
     */
    op: z14.literal("move_day"),
    fromDayIndex: z14.number().int().min(1),
    fromDate: patchDate,
    fromDayTitle: patchDayTitle,
    /** The slot it goes to, by its number before this patch. */
    toDayIndex: z14.number().int().min(1),
    toDate: patchDate
  }),
  z14.object({ op: z14.literal("note"), text: z14.string().max(600) })
]);
var AiPatchDraft = z14.object({
  operations: z14.array(AiPatchOp).max(50).default([]),
  summary: z14.string().max(1e3).nullable().default(null)
});
var AiJob = z14.object({
  id: z14.string().uuid(),
  tripId: z14.string().uuid().nullable(),
  userId: z14.string().uuid(),
  kind: AiJobKind,
  status: AiJobStatus,
  prompt: z14.string(),
  model: z14.string().nullable(),
  /** Populated on success: AiTripDraft or AiPatchDraft, depending on `kind`. */
  result: z14.unknown().nullable(),
  error: z14.string().nullable(),
  /** Rough token usage, for the admin dashboard and per-user quotas. */
  promptTokens: z14.number().int().nonnegative().nullable(),
  completionTokens: z14.number().int().nonnegative().nullable(),
  durationMs: z14.number().int().nonnegative().nullable(),
  createdAt: IsoDateTime,
  finishedAt: IsoDateTime.nullable()
});
var AiModelInfo = z14.object({
  id: z14.string(),
  label: z14.string(),
  contextLength: z14.number().int().positive(),
  /**
   * Which upstream serves it, as the hub reports.
   *
   * A free string, not an enum: which providers exist is the hub's business
   * now, and adding one there must not require a change to this contract and
   * a redeploy of every client. It was `'nvidia' | 'openrouter'` back when
   * this API carried the provider glue itself.
   */
  provider: z14.string().default("unknown"),
  /** True where zero pricing was verified against the live catalogue. */
  isFree: z14.boolean().default(false),
  supportsJson: z14.boolean().default(false),
  recommendedFor: z14.array(AiJobKind).default([])
});
var AiChatRequest = z14.object({
  message: z14.string().trim().max(4e3).default(""),
  context: z14.object({
    activityId: z14.string().uuid().optional(),
    placeId: z14.string().uuid().optional(),
    dayId: z14.string().uuid().optional()
  }).optional(),
  /**
   * Files sent with the message — tickets, vouchers, rental confirmations,
   * already uploaded to the trip's documents. They are read and filed against
   * the bookings they prove before the message itself is handled.
   */
  documentIds: z14.array(z14.string().uuid()).max(10).default([]),
  /**
   * The device's clock and zone when the message was sent. On the road it
   * is where the traveller is, so «мы уже у Фридландских ворот» re-plans
   * from now — the server's UTC day is not the traveller's.
   */
  clientTime: z14.object({
    now: z14.string().datetime({ offset: true }),
    timeZone: z14.string().max(64).nullable().default(null)
  }).optional()
}).refine((body) => body.message.length >= 2 || body.documentIds.length > 0, {
  message: "\u041D\u0430\u043F\u0438\u0448\u0438\u0442\u0435 \u0441\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435 \u0438\u043B\u0438 \u043F\u0440\u0438\u043B\u043E\u0436\u0438\u0442\u0435 \u0444\u0430\u0439\u043B",
  path: ["message"]
});
var AiMemoryRule = z14.object({
  text: z14.string().min(2).max(200),
  /** When it was said — the newer of two contradicting wishes wins. */
  at: z14.string()
});
var AiMemory = z14.object({
  rules: z14.array(AiMemoryRule).max(20).catch([]).default([]),
  summary: z14.string().max(3e3).nullable().catch(null).default(null),
  /** `createdAt` of the newest chat job the summary covers. */
  summarizedThrough: z14.string().nullable().catch(null).default(null)
});
var AiChatMemoryView = z14.object({
  rules: z14.array(z14.string()),
  summary: z14.string().nullable()
});
var AiMemoryPatch = z14.object({
  rules: z14.array(z14.string().trim().min(2).max(200)).max(20)
});
var AI_CHAT_INTENTS = ["answer", "edit", "plan_day", "recommend", "places", "prices", "extend", "research", "flights", "visa", "hotels", "documents"];
var AiChatIntent = z14.enum(AI_CHAT_INTENTS);
var AiChatTurn = z14.object({
  id: z14.string().uuid(),
  kind: z14.string(),
  createdAt: z14.string(),
  author: z14.string().nullable(),
  mine: z14.boolean(),
  message: z14.string(),
  /** «О пункте „Пляж Капуташ“» — what the message was sent from. */
  contextLabel: z14.string().nullable(),
  /** Files sent with the message. */
  attachments: z14.array(z14.object({ id: z14.string().uuid(), title: z14.string() })).default([]),
  status: z14.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
  intent: z14.string().nullable(),
  /** While working: what the agent said it is doing. */
  ack: z14.string().nullable(),
  /** Expected seconds for the work, counted from `workStartedAt`. */
  etaSeconds: z14.number().nullable(),
  /** While working: the step under way — «Смотрю часы работы…», «Собираю день…». */
  stage: z14.string().nullable().default(null),
  workStartedAt: z14.string().nullable(),
  /** Generation progress, for the long plan/extend runs. */
  progress: z14.object({ total: z14.number(), completed: z14.number() }).nullable(),
  reply: z14.string().nullable(),
  details: z14.array(z14.string()),
  sources: z14.array(z14.object({ title: z14.string(), url: z14.string() })),
  error: z14.string().nullable(),
  /** This turn changed the trip and is the latest such step not yet undone. */
  canRevert: z14.boolean(),
  revertedAt: z14.string().nullable(),
  durationMs: z14.number().nullable(),
  /**
   * A plan shown and not applied («Пока ничего не меняла»): what it would
   * change, line by line, and whether it can still be applied — only the
   * newest, and only while nothing has changed the trip since.
   */
  proposal: z14.object({
    lines: z14.array(z14.string()),
    status: z14.enum(["pending", "applied", "dismissed", "stale"])
  }).nullable().default(null)
});

// src/schemas/sync.ts
import { z as z15 } from "zod";
var SYNC_ENTITIES = [
  "trip",
  "day",
  "activity",
  "place",
  "reservation",
  "photo",
  "document"
];
var SyncEntity = z15.enum(SYNC_ENTITIES);
var SyncOperation = z15.enum(["upsert", "delete"]);
var SyncChange = z15.object({
  entity: SyncEntity,
  op: SyncOperation,
  id: z15.string().uuid(),
  tripId: z15.string().uuid(),
  /** The rev the client based this edit on. 0 for a row created offline. */
  baseRev: z15.number().int().nonnegative().default(0),
  /** Client wall clock — advisory only; the server timestamps authoritatively. */
  clientUpdatedAt: IsoDateTime.optional(),
  data: z15.record(z15.unknown()).default({})
});
var SyncPushRequest = z15.object({
  /** Identifies the device so pull can skip echoing its own writes back. */
  deviceId: z15.string().min(6).max(64),
  changes: z15.array(SyncChange).max(500)
});
var SyncConflict = z15.object({
  entity: SyncEntity,
  id: z15.string().uuid(),
  reason: z15.enum(["stale_rev", "deleted_on_server", "forbidden", "invalid"]),
  serverRev: z15.number().int().nonnegative().nullable(),
  serverData: z15.record(z15.unknown()).nullable()
});
var SyncPushResponse = z15.object({
  applied: z15.array(
    z15.object({
      entity: SyncEntity,
      id: z15.string().uuid(),
      rev: z15.number().int().nonnegative(),
      updatedAt: IsoDateTime
    })
  ),
  conflicts: z15.array(SyncConflict),
  cursor: z15.string()
});
var SyncPullQuery = z15.object({
  tripId: z15.string().uuid().optional(),
  since: z15.string().optional(),
  deviceId: z15.string().min(6).max(64).optional(),
  limit: z15.coerce.number().int().min(1).max(1e3).default(500)
});
var SyncPullResponse = z15.object({
  changes: z15.array(
    z15.object({
      entity: SyncEntity,
      op: SyncOperation,
      id: z15.string().uuid(),
      tripId: z15.string().uuid(),
      rev: z15.number().int().nonnegative(),
      updatedAt: IsoDateTime,
      data: z15.record(z15.unknown()).nullable()
    })
  ),
  cursor: z15.string(),
  hasMore: z15.boolean(),
  /** Server time, so clients can detect clock skew before trusting their own. */
  serverTime: IsoDateTime
});

// src/schemas/admin.ts
import { z as z16 } from "zod";
var InviteCode = z16.object({
  id: z16.string().uuid(),
  code: z16.string(),
  kind: InviteKind.default("admin"),
  label: z16.string().nullable(),
  maxUses: z16.number().int().min(1).nullable(),
  usedCount: z16.number().int().nonnegative(),
  expiresAt: IsoDateTime.nullable(),
  disabledAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  createdById: z16.string().uuid().nullable(),
  /** Role granted to accounts created with this code. */
  grantsRole: UserRole.default("user"),
  /** True when the code still accepts a registration. */
  available: z16.boolean().default(true)
});
var InviteCodeCreateInput = z16.object({
  code: z16.string().trim().min(4).max(64).optional(),
  label: z16.string().max(120).optional(),
  maxUses: z16.number().int().min(1).max(1e5).nullable().optional(),
  expiresInDays: z16.number().int().min(1).max(3650).nullable().optional(),
  grantsRole: UserRole.default("user")
});
var InviteRequestCreateInput = z16.object({
  preferredHandle: z16.string().trim().toLowerCase().max(32).optional(),
  note: z16.string().trim().max(280).optional()
});
var InviteRequestCreated = z16.object({
  id: z16.string().uuid(),
  /** Shown once. Store it — it is the only way to retrieve an approved code. */
  claimToken: z16.string(),
  status: z16.literal("pending")
});
var InviteRequestClaim = z16.object({
  id: z16.string().uuid(),
  status: InviteRequestStatus,
  inviteCode: z16.string().nullable(),
  rejectReason: z16.string().nullable(),
  createdAt: IsoDateTime,
  resolvedAt: IsoDateTime.nullable()
});
var AdminInviteRequestRow = z16.object({
  id: z16.string().uuid(),
  preferredHandle: z16.string().nullable(),
  note: z16.string().nullable(),
  status: InviteRequestStatus,
  inviteCode: z16.string().nullable(),
  rejectReason: z16.string().nullable(),
  createdAt: IsoDateTime,
  resolvedAt: IsoDateTime.nullable(),
  resolvedById: z16.string().uuid().nullable()
});
var AdminUserRow = z16.object({
  id: z16.string().uuid(),
  handle: z16.string(),
  displayName: z16.string().nullable(),
  role: UserRole,
  tripCount: z16.number().int().nonnegative(),
  photoCount: z16.number().int().nonnegative(),
  aiJobCount: z16.number().int().nonnegative(),
  disabledAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  lastSeenAt: IsoDateTime.nullable()
});
var AdminUserUpdateInput = z16.object({
  role: UserRole.optional(),
  disabled: z16.boolean().optional(),
  /** Per-user AI budget; null means "use the global default". */
  aiDailyLimit: z16.number().int().min(0).max(1e3).nullable().optional(),
  /** Support/moderation override — e.g. a name that turned out to be someone's real one. */
  displayName: z16.string().max(60).nullable().optional()
});
var AdminApiTokenRow = z16.object({
  id: z16.string().uuid(),
  /** Whose it is — a token acts as that user, with that user's role. */
  handle: z16.string(),
  prefix: z16.string(),
  label: z16.string().nullable(),
  expiresAt: IsoDateTime.nullable(),
  revokedAt: IsoDateTime.nullable(),
  lastUsedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime
});
var AdminApiTokenCreateInput = z16.object({
  label: z16.string().trim().max(120).optional(),
  /** Defaults to the calling admin; another user makes a token acting as them. */
  userId: z16.string().uuid().optional(),
  expiresInDays: z16.number().int().min(1).max(365).nullable().optional()
});
var AdminApiTokenCreated = AdminApiTokenRow.extend({
  /** Shown once. Not stored in plaintext, so it cannot be shown again. */
  token: z16.string()
});
var AdminStats = z16.object({
  users: z16.number().int().nonnegative(),
  trips: z16.number().int().nonnegative(),
  publicTrips: z16.number().int().nonnegative(),
  places: z16.number().int().nonnegative(),
  photos: z16.number().int().nonnegative(),
  storageBytes: z16.number().int().nonnegative(),
  aiJobs24h: z16.number().int().nonnegative(),
  aiFailures24h: z16.number().int().nonnegative(),
  activeInvites: z16.number().int().nonnegative(),
  pendingInviteRequests: z16.number().int().nonnegative()
});
var AdminVisitSession = z16.object({
  id: z16.string().uuid(),
  userId: z16.string().uuid().nullable(),
  ip: z16.string(),
  forwardedFor: z16.string().nullable(),
  hostname: z16.string().nullable(),
  userAgent: z16.string().nullable(),
  isBot: z16.boolean(),
  botName: z16.string().nullable(),
  browser: z16.string().nullable(),
  os: z16.string().nullable(),
  device: z16.string().nullable(),
  acceptLanguage: z16.string().nullable(),
  referer: z16.string().nullable(),
  entryPath: z16.string().nullable(),
  paths: z16.array(z16.string()),
  /** What the page reported about itself: time zone, screen, languages. */
  client: z16.record(z16.unknown()).nullable(),
  hitCount: z16.number().int(),
  viewCount: z16.number().int(),
  apiCount: z16.number().int(),
  startedAt: IsoDateTime,
  lastSeenAt: IsoDateTime
});
var AdminVisitor = z16.object({
  kind: z16.enum(["user", "guest"]),
  userId: z16.string().uuid().nullable(),
  handle: z16.string().nullable(),
  displayName: z16.string().nullable(),
  /** The address of the latest visit; for a guest, the address itself. */
  ip: z16.string(),
  ipCount: z16.number().int(),
  ips: z16.array(z16.string()),
  hostname: z16.string().nullable(),
  userAgent: z16.string().nullable(),
  isBot: z16.boolean(),
  botName: z16.string().nullable(),
  browser: z16.string().nullable(),
  os: z16.string().nullable(),
  device: z16.string().nullable(),
  sessionCount: z16.number().int(),
  hitCount: z16.number().int(),
  viewCount: z16.number().int(),
  apiCount: z16.number().int(),
  firstSeenAt: IsoDateTime,
  lastSeenAt: IsoDateTime
});
var AdminVisitorsQuery = z16.object({
  filter: z16.enum(["all", "users", "guests", "bots"]).default("all"),
  days: z16.coerce.number().int().min(1).max(3650).default(7),
  q: z16.string().trim().max(120).optional(),
  limit: z16.coerce.number().int().min(1).max(500).default(200)
});
var AdminVisitorsResponse = z16.object({
  items: z16.array(AdminVisitor),
  totals: z16.object({
    visitors: z16.number().int(),
    users: z16.number().int(),
    guests: z16.number().int(),
    bots: z16.number().int(),
    sessions: z16.number().int()
  })
});

// src/schemas/maps.ts
import { z as z17 } from "zod";
var MapsConfig = z17.object({
  yandexApiKey: z17.string().nullable(),
  googleApiKey: z17.string().nullable()
});
var Point = z17.object({ lat: Latitude, lng: Longitude });
var RoadsInput = z17.object({
  legs: z17.array(z17.object({ from: Point, to: Point })).min(1).max(120)
});
var RoadLegSchema = z17.object({
  key: z17.string(),
  mode: z17.enum(["foot", "car", "straight"]),
  path: z17.array(z17.tuple([z17.number(), z17.number()])),
  distanceM: z17.number(),
  durationS: z17.number(),
  straight: z17.boolean(),
  pending: z17.boolean()
});
var RoadsResult = z17.object({ legs: z17.array(RoadLegSchema) });
var MAP_LIST_PROVIDERS = ["google", "yandex"];
var MapListLink = z17.object({
  id: z17.string().uuid(),
  provider: z17.enum(MAP_LIST_PROVIDERS),
  url: z17.string(),
  title: z17.string().nullable(),
  itemCount: z17.number().int(),
  importedCount: z17.number().int(),
  lastSyncedAt: IsoDateTime.nullable(),
  lastError: z17.string().nullable(),
  createdAt: IsoDateTime
});
var MapListLinkInput = z17.object({ url: z17.string().trim().url().max(2e3) });
var MapListSyncResult = z17.object({
  link: MapListLink,
  added: z17.array(z17.object({ placeId: z17.string().uuid(), name: z17.string(), dayIndex: z17.number().int().nullable() }))
});
var PlaceFromLinkInput = z17.object({
  url: z17.string().trim().url().max(2e3),
  name: z17.string().trim().min(1).max(200).optional(),
  /** Put on this day; omitted → the nearest day, or none. */
  dayId: z17.string().uuid().nullable().optional()
});
var YandexList = z17.object({
  id: z17.string().uuid(),
  dayId: z17.string().uuid().nullable(),
  dayIndex: z17.number().int().nullable(),
  url: z17.string().nullable(),
  itemCount: z17.number().int(),
  pushedAt: IsoDateTime.nullable(),
  lastError: z17.string().nullable()
});
var YandexListsResult = z17.object({
  /** The server has a Yandex account to keep lists in. */
  available: z17.boolean(),
  lists: z17.array(YandexList)
});
export {
  ABKHAZIA,
  ACTIVITY_KINDS,
  AI_CHAT_INTENTS,
  AI_JOB_KINDS,
  AI_JOB_STATUSES,
  Activity,
  ActivityBase,
  ActivityCreateInput,
  ActivityKind,
  ActivityUpdateInput,
  AdminApiTokenCreateInput,
  AdminApiTokenCreated,
  AdminApiTokenRow,
  AdminInviteRequestRow,
  AdminStats,
  AdminUserRow,
  AdminUserUpdateInput,
  AdminVisitSession,
  AdminVisitor,
  AdminVisitorsQuery,
  AdminVisitorsResponse,
  AiActivityDraft,
  AiBookingContext,
  AiChatIntent,
  AiChatMemoryView,
  AiChatRequest,
  AiChatTurn,
  AiDayDraft,
  AiEditRequest,
  AiIngestRequest,
  AiIngestResult,
  AiJob,
  AiJobKind,
  AiJobStatus,
  AiMemory,
  AiMemoryPatch,
  AiMemoryRule,
  AiModelInfo,
  AiParseDocumentsRequest,
  AiParseDocumentsResult,
  AiParsedDocument,
  AiPatchDraft,
  AiPatchOp,
  AiPlaceDraft,
  AiPlanRequest,
  AiReservationDraft,
  AiTripDraft,
  AiVisaRequest,
  ApiError,
  AuthSession,
  AuthTokens,
  BUDGET_CATEGORIES,
  CAVEAT_KINDS,
  CaveatKind,
  ChangePasswordInput,
  ClockTime,
  CostPart,
  DEAL_FILTER_KEYS,
  DEAL_KINDS,
  DEAL_MEALS,
  DEAL_ORIGINS,
  DOCUMENT_GROUPS,
  DayTimesInput,
  DealConfirmInput,
  DealDetails,
  DealKind,
  DealMeal,
  DealOrigin,
  DealPlanRequest,
  DealSearchRequest,
  DealSearchResponse,
  DealSearchResult,
  DealSearchStatus,
  DealVerification,
  DocumentGroup,
  ExternalLink,
  FIXED_ACTIVITY_KINDS,
  FLIGHT_STATE_LABELS,
  FlightLink,
  FlightState,
  FlightStatus,
  FlightStatusEntry,
  GOOGLE_MAX_WAYPOINTS,
  GeoPoint,
  HANDLE_PATTERN,
  Handle,
  INVITE_KINDS,
  INVITE_REQUEST_STATUSES,
  InviteCode,
  InviteCodeCreateInput,
  InviteKind,
  InviteRequestClaim,
  InviteRequestCreateInput,
  InviteRequestCreated,
  InviteRequestStatus,
  IsoDate,
  IsoDateTime,
  LatLng,
  Latitude,
  LoginInput,
  Longitude,
  MAP_ENGINES,
  MAP_LIST_PROVIDERS,
  MapListLink,
  MapListLinkInput,
  MapListSyncResult,
  MapsConfig,
  Money,
  NIGHTS_MAX,
  NIGHTS_MIN,
  NO_DEAL_FILTERS,
  OkResponse,
  PERSONAL_INVITE_GRANT,
  PHOTO_VISIBILITIES,
  PLACE_KINDS,
  PaginationQuery,
  Password,
  Photo,
  PhotoAssignment,
  PhotoBase,
  PhotoBulkUpdateInput,
  PhotoIngestResult,
  PhotoUpdateInput,
  PhotoVisibility,
  Place,
  PlaceBase,
  PlaceCaveat,
  PlaceCreateInput,
  PlaceFromLinkInput,
  PlaceHours,
  PlaceKind,
  PlaceReview,
  PlaceTicket,
  PlaceUpdateInput,
  PrivacyProfile,
  PublicActivity,
  PublicPhoto,
  PublicPlace,
  PublicReservation,
  PublicTrip,
  PublicTripDay,
  PublicUser,
  RESERVATION_KINDS,
  RESERVATION_STATUSES,
  REVIEW_CATEGORIES,
  ROAD_MAX_M,
  RecoverInput,
  RegisterInput,
  Reservation,
  ReservationBase,
  ReservationConfidential,
  ReservationCreateInput,
  ReservationKind,
  ReservationStatus,
  ReservationUpdateInput,
  ReviewCategory,
  RoadLegSchema,
  RoadsInput,
  RoadsResult,
  SOON_DAYS,
  STAR_FLOORS,
  SYNC_ENTITIES,
  SyncChange,
  SyncConflict,
  SyncEntity,
  SyncMeta,
  SyncOperation,
  SyncPullQuery,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  TRAVEL_STYLES,
  TRIP_PACES,
  TRIP_STATUSES,
  TRIP_VISIBILITIES,
  TravelDeal,
  TravelStyle,
  Trip,
  TripBase,
  TripChangeEntry,
  TripCreateInput,
  TripDay,
  TripDayBase,
  TripDayCreateInput,
  TripDayUpdateInput,
  TripDeal,
  TripDetail,
  TripDocument,
  TripDocumentBase,
  TripDocumentUpdateInput,
  TripForkInput,
  TripInviteAccepted,
  TripInviteCreateInput,
  TripInvitePreview,
  TripInviteRow,
  TripListQuery,
  TripMemberCreateInput,
  TripMemberRow,
  TripMemberUpdateInput,
  TripPace,
  TripPreferences,
  TripShare,
  TripShareCreateInput,
  TripStatus,
  TripSummary,
  TripUpdateInput,
  TripVisibility,
  USER_ROLES,
  User,
  UserPreferences,
  UserRole,
  Uuid,
  ViewerContext,
  ViewerRole,
  VisaInfo,
  VisaRequirement,
  WALK_MAX_M,
  YANDEX_MAX_POINTS,
  YandexList,
  YandexListsResult,
  activeFilters,
  addIsoDays,
  addressTowns,
  airportCodeOf,
  airportName,
  appRouteUrl,
  boundsOf,
  buildGpx,
  buildKml,
  canEstimateIn,
  categoriseActivity,
  categorisePlace,
  categoriseReservation,
  caveatConflicts,
  centroidOf,
  chronologicalIndex,
  chunkRoute,
  convertMoney,
  countryNameRu,
  currentShiftItem,
  dayForPoint,
  dealNights,
  defaultPrivacyProfile,
  departureDelay,
  driveEstimate,
  driveMinutes,
  escapeXml,
  estimateDailySpend,
  estimateDriveMinutes,
  estimateFlightCost,
  estimateItemCost,
  fillVisibleSlots,
  filterDeals,
  flightAwareUrl,
  flightDateOf,
  flightIdentOf,
  flightIdentOfReservation,
  flightLinks,
  flightStatusChanges,
  footMinutes,
  formatDistance,
  formatFlightIdent,
  formatMinutes,
  formatTripDateLabel,
  fromUsd,
  gpxFilename,
  gpxSymbolFor,
  groupFoodPlaces,
  guessOffsetMinutes,
  guessPlaceKind,
  guestViewer,
  haversineMeters,
  hoursConflict,
  hoursOn,
  inNights,
  isActive,
  isMapShortLink,
  isUnofficialCountry,
  isoWeekday,
  joinTimes,
  kindGroup,
  legKey,
  legMode,
  legsOf,
  localClock,
  localIsoDate,
  lodgingLinks,
  mapsSearchLink,
  minutesBetween,
  minutesOf2 as minutesOf,
  nearest,
  nightsSpan,
  openPrivacyProfile,
  paginated,
  partySize,
  pickActiveTrip,
  placeByTime,
  placeHoursOn,
  pointFromMapUrl,
  projectTrip,
  randomToken,
  rankFoodGroups,
  reviewAverage,
  routeSearchUrl,
  seesPrivateData,
  shiftDay,
  simplifyPath,
  slugify,
  sortCaveats,
  sortOrderAt,
  summariseBudget,
  summarisePlanFact,
  sunTimes,
  timeForSlot,
  timeOf,
  timesAfterReorder,
  travelLinks,
  tripMediaUrls,
  tripPhase,
  uniquePassengers,
  uuidv7,
  visitFits,
  walkMinutes,
  weekdaysLabel,
  yandexPinsUrl,
  zoneOffsetMinutes,
  zoomToFit
};
//# sourceMappingURL=index.js.map